import React, { useState, useEffect, useMemo, useRef } from 'react';
import { can, isAdminOrPlatform, isTenantAdmin } from '../lib/access';
import { useSearchParams } from 'react-router-dom';
import KnowledgeAttentionPanel from '../components/KnowledgeAttentionPanel';
import ReviewScheduleFields, { EMPTY_REVIEW_SCHEDULE, reviewSchedulePayload } from '../components/ReviewScheduleFields';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { ExpandableTextarea } from '../components/ui/expandable-textarea';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { 
  Plus, Edit, Trash2, BookOpen, ClipboardList, Upload, Link, Hash, Copy,
  ChevronRight, ChevronDown, Search, Filter, AlertTriangle, CheckCircle,
  Clock, TrendingUp, Archive, Eye, BarChart3, Shield, Layers, X,
  Building2, Briefcase, Users, FolderOpen, Settings, FileText, ScrollText, GitBranch,
  RefreshCw, Loader2, Info, Grid3X3, List
} from 'lucide-react';
import KnowledgeUploadLauncher from '../components/KnowledgeUploadLauncher';
import KnowledgeEditor from '../components/KnowledgeEditor';
import KnowledgeBadgePicker from '../components/KnowledgeBadgePicker';
import { IconFromString } from '../components/IconFromString';
import LinkedSourceAccessFields from '../components/LinkedSourceAccessFields';
import LinkedSourcePreview from '../components/LinkedSourcePreview';
import { HubSourceShortcuts } from '../components/sources/KnowledgeSources';

// ============= SOURCE TYPE DEFINITIONS =============
const SOURCE_TYPES = {
  note: {
    id: 'note',
    name: 'Note',
    icon: '📝',
    iconComponent: BookOpen,
    color: 'blue',
    description: 'Quick reference',
    fields: ['title', 'description', 'examples', 'linked_docs', 'linked_forms', 'team_id'],
    requiredFields: ['title', 'description'],
  },
  resolution: {
    id: 'resolution',
    name: 'Resolution Guide',
    icon: '📋',
    iconComponent: ClipboardList,
    color: 'emerald',
    description: 'Step-by-step',
    fields: ['title', 'keywords', 'description', 'resolution_steps', 'email_template', 'contacts', 'ticket_reference', 'sla_hours', 'sla_details', 'linked_docs', 'linked_forms', 'team_id'],
    requiredFields: ['title', 'keywords', 'description', 'resolution_steps'],
  },
  policy: {
    id: 'policy',
    name: 'Policy',
    icon: '📜',
    iconComponent: ScrollText,
    color: 'purple',
    description: 'Rules & guidelines',
    fields: ['title', 'keywords', 'description', 'policy_content', 'effective_date', 'version', 'owner', 'review_date', 'linked_docs', 'linked_forms', 'team_id'],
    requiredFields: ['title', 'description', 'policy_content'],
  },
  process: {
    id: 'process',
    name: 'Process',
    icon: '🔄',
    iconComponent: GitBranch,
    color: 'amber',
    description: 'Workflow steps',
    fields: ['title', 'keywords', 'description', 'process_steps', 'process_owner', 'department', 'frequency', 'tools_required', 'linked_docs', 'linked_forms', 'team_id'],
    requiredFields: ['title', 'description', 'process_steps'],
  },
};

const SOURCE_COLORS = {
  note: { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-400', light: 'bg-blue-50' },
  resolution: { bg: 'bg-emerald-100', text: 'text-emerald-700', border: 'border-emerald-400', light: 'bg-emerald-50' },
  policy: { bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-400', light: 'bg-purple-50' },
  process: { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-400', light: 'bg-amber-50' },
  template: { bg: 'bg-emerald-100', text: 'text-emerald-700', border: 'border-emerald-400', light: 'bg-emerald-50' }, // Legacy
};

// ============= HELPER FUNCTIONS =============
const formatTimeAgo = (date) => {
  if (!date) return 'Unknown';
  const days = Math.floor((Date.now() - new Date(date).getTime()) / (1000 * 60 * 60 * 24));
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  if (days < 30) return `${Math.floor(days / 7)} weeks ago`;
  return `${Math.floor(days / 30)} months ago`;
};

const getIntent = (item) => {
  const title = (item.title || '').toLowerCase();
  const desc = (item.description || '').toLowerCase();
  if (title.includes('what is') || title.includes('definition') || desc.includes('definition')) return 'Definition';
  if (title.includes('how to') || title.includes('process') || title.includes('steps') || item.resolution_steps?.length > 0) return 'Resolution';
  if (title.includes('impact') || title.includes('risk') || title.includes('why')) return 'Impact';
  return 'General';
};

const getUsageLevel = (item) => {
  const daysSinceUpdate = Math.floor((Date.now() - new Date(item.updated_at || Date.now()).getTime()) / (1000 * 60 * 60 * 24));
  if (daysSinceUpdate < 7) return 'High';
  if (daysSinceUpdate < 30) return 'Medium';
  if (daysSinceUpdate < 90) return 'Low';
  return 'Unused';
};

const getSourceTypeDisplay = (assetType) => {
  if (assetType === 'template') return 'resolution'; // Map legacy type
  return assetType || 'note';
};

const InfoHint = ({ label, children }) => (
  <TooltipProvider delayDuration={150}>
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={label}
          className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-500 hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700"
        >
          <Info size={12} />
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-xs text-xs leading-relaxed">
        {children}
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

const formatCompactTimeAgo = (date) => {
  if (!date) return 'Unknown';
  const days = Math.floor((Date.now() - new Date(date).getTime()) / (1000 * 60 * 60 * 24));
  if (days === 0) return 'Today';
  if (days === 1) return '1d ago';
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  return `${Math.floor(days / 30)}mo ago`;
};

const getIndexStatusMeta = (item) => {
  const rawStatus = (item.indexing_status || '').toLowerCase();
  const itemStatus = (item.status || '').toLowerCase();
  const chunks = item.chunk_count ?? item.chunks_count ?? item.indexed_chunks ?? 0;

  if (itemStatus === 'draft' || rawStatus === 'draft') {
    return {
      label: 'Draft',
      indexLabel: 'Not indexed',
      dot: 'bg-gray-400',
      icon: 'bg-gray-100 text-gray-600',
      sortRank: 2,
    };
  }

  if (rawStatus === 'indexing') {
    return {
      label: 'Indexing',
      indexLabel: 'Indexing',
      dot: 'bg-blue-500',
      icon: 'bg-blue-100 text-blue-700',
      sortRank: 1,
    };
  }

  if (rawStatus === 'failed') {
    return {
      label: 'Index failed',
      indexLabel: 'Failed',
      dot: 'bg-rose-500',
      icon: 'bg-rose-100 text-rose-700',
      sortRank: 3,
    };
  }

  if (chunks > 0 || rawStatus === 'ready' || rawStatus === 'indexed' || item.is_indexed) {
    return {
      label: 'Indexed / active',
      indexLabel: chunks > 0 ? `${chunks} indexed` : 'Indexed',
      dot: 'bg-emerald-500',
      icon: 'bg-emerald-100 text-emerald-700',
      sortRank: 0,
    };
  }

  return {
    label: 'Unused',
    indexLabel: 'Not indexed',
    dot: 'bg-amber-500',
    icon: 'bg-amber-100 text-amber-700',
    sortRank: 4,
  };
};

const StatusDot = ({ item }) => {
  const status = getIndexStatusMeta(item);
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={`inline-flex h-2.5 w-2.5 rounded-full ${status.dot}`}
            aria-label={status.label}
          />
        </TooltipTrigger>
        <TooltipContent side="top" className="text-xs">
          {status.label}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

// ============= MAIN COMPONENT =============
const ResolutionManagement = ({ user, setUser }) => {
  const [searchParams] = useSearchParams();
  const canManageSharedCategories = can(user, 'can_manage_knowledge');
  const handledSourceLink = useRef('');
  const [resolutions, setResolutions] = useState([]);
  const [forms, setForms] = useState([]);
  const [opTeams, setOpTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Category state
  const [categoryTree, setCategoryTree] = useState([]);
  const [generalCategories, setGeneralCategories] = useState([]);
  const [categoryStats, setCategoryStats] = useState({ sectors: 0, departments: 0, teams: 0, general_categories: 0 });
  const [expandedSectors, setExpandedSectors] = useState({});
  const [expandedDepts, setExpandedDepts] = useState({});
  const [expandedGeneral, setExpandedGeneral] = useState(false);
  const [expandedKnowledgeCategories, setExpandedKnowledgeCategories] = useState({});
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [selectedGeneralCategory, setSelectedGeneralCategory] = useState(null);
  
  // Dialog states
  const [isSourceDialogOpen, setIsSourceDialogOpen] = useState(false);
  const [isSectorDialogOpen, setIsSectorDialogOpen] = useState(false);
  const [isGeneralCategoryDialogOpen, setIsGeneralCategoryDialogOpen] = useState(false);
  const [isDeptDialogOpen, setIsDeptDialogOpen] = useState(false);
  const [isTeamDialogOpen, setIsTeamDialogOpen] = useState(false);
  const [editingResolution, setEditingResolution] = useState(null);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [initialVersionId, setInitialVersionId] = useState(null);
  const [uploadingForm, setUploadingForm] = useState(false);
  
  // Category form data
  const [sectorData, setSectorData] = useState({ name: '', icon: '🏢', description: '' });
  const [generalCategoryData, setGeneralCategoryData] = useState({ name: '', icon: '🌐', description: '' });
  const [deptData, setDeptData] = useState({ name: '', sector_id: '', icon: '💼', description: '' });
  const [teamData, setTeamData] = useState({ name: '', department_id: '', icon: '👥', description: '' });
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingCategoryType, setEditingCategoryType] = useState(null);
  
  // Auto-Extract state
  const [showAutoExtract, setShowAutoExtract] = useState(false);
  const [rawInput, setRawInput] = useState('');
  const [extracting, setExtracting] = useState(false);
  const [testingLinkedFetch, setTestingLinkedFetch] = useState(false);
  const [linkedFetchState, setLinkedFetchState] = useState(null);
  const [showExtractionAdvanced, setShowExtractionAdvanced] = useState(false);

  // Access Control (Admin RBAC)
  const [accessControlOpen, setAccessControlOpen] = useState(false);
  const [rbacUsers, setRbacUsers] = useState([]);
  const [rbacSelectedUserId, setRbacSelectedUserId] = useState('');
  const [rbacUserSearch, setRbacUserSearch] = useState('');
  const [rbacFolderPerms, setRbacFolderPerms] = useState([]);
  const [rbacFilePerms, setRbacFilePerms] = useState([]);
  const [rbacLoading, setRbacLoading] = useState(false);

  // Current user's effective permissions (used to gate Upload/Edit/Delete for non-admins)
  const emptyPermissions = { unrestricted: false, can_delete_unrestricted: false, folder_permissions: [], file_permissions: [] };
  const [myPermissions, setMyPermissions] = useState(emptyPermissions);

  // Data Source Integrations (moved from System)
  const [dataSourceInfo, setDataSourceInfo] = useState(null);

  // Search & Filter
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedKnowledgeItemId, setSelectedKnowledgeItemId] = useState(null);
  const [filterBySourceType, setFilterBySourceType] = useState(null); // Filter by source type
  const [itemView, setItemView] = useState(() => {
    try {
      return localStorage.getItem('knowledgeItemView') || 'grid';
    } catch {
      return 'grid';
    }
  });
  const [itemSort, setItemSort] = useState(() => {
    try {
      return localStorage.getItem('knowledgeItemSort') || 'recent';
    } catch {
      return 'recent';
    }
  });
  const [itemPage, setItemPage] = useState(1);
  const [itemPageSize, setItemPageSize] = useState(() => {
    try {
      const stored = Number(localStorage.getItem('knowledgeItemPageSize'));
      return [25, 50, 100].includes(stored) ? stored : 25;
    } catch {
      return 25;
    }
  });
  const [expandedGroups, setExpandedGroups] = useState({});

  // Custom Source Types state
  const [customSourceTypes, setCustomSourceTypes] = useState([]);
  const [isSourceTypeDialogOpen, setIsSourceTypeDialogOpen] = useState(false);
  const [editingSourceType, setEditingSourceType] = useState(null);
  const [sourceTypeForm, setSourceTypeForm] = useState({
    name: '',
    icon: '📄',
    color: 'gray',
    description: '',
    custom_fields: []
  });
  const [newCustomField, setNewCustomField] = useState({ name: '', label: '', field_type: 'text', required: false, placeholder: '' });

  // Unified Source Form Data
  const [selectedSourceType, setSelectedSourceType] = useState('note');
  const [sourceTeamPickerOpen, setSourceTeamPickerOpen] = useState(false);
  const [sourceCategoryPickerOpen, setSourceCategoryPickerOpen] = useState(false);
  const [sourceDatesOpen, setSourceDatesOpen] = useState(false);
  const [sourceFormData, setSourceFormData] = useState({
    title: '',
    keywords: '',
    description: '',
    examples: '',
    resolution_steps: '',
    email_template: '',
    contacts: '',
    ticket_reference: '',
    sla_hours: '',
    sla_details: '',
    policy_content: '',
    effective_date: '',
    version: '',
    owner: '',
    review_date: '',
    process_steps: '',
    process_owner: '',
    department: '',
    frequency: '',
    tools_required: '',
    linked_docs: '',
    linked_forms: [],
    team_id: null,
    custom_fields: [],
    source_type: 'static',
    source_url: '',
    refresh_interval: 'manual_check_only',
    extraction_scope: 'full_page',
    custom_selector: '',
    source_auth_method: 'public',
    source_auth: {},
    document_type: '',
    category_id: null,
    badge_ids: [],
    visibility: 'public',
    allowed_roles: '',
    allowed_teams: '',
    source_published_at: '',
    effective_from: '',
    effective_until: '',
    last_reviewed_at: '',
    version_label: '',
    review_schedule: EMPTY_REVIEW_SCHEDULE,
  });

  useEffect(() => {
    fetchAll();
    fetchDataSourceInfo();
    // Fetch current user's KH permissions (enforcement for non-admins / Source Leaders)
    axiosInstance.get('/rbac/my-permissions')
      .then(r => setMyPermissions({ ...emptyPermissions, ...r.data }))
      .catch(() => setMyPermissions(emptyPermissions));
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem('knowledgeItemView', itemView);
    } catch {}
  }, [itemView]);

  useEffect(() => {
    try {
      localStorage.setItem('knowledgeItemSort', itemSort);
    } catch {}
  }, [itemSort]);

  useEffect(() => {
    try {
      localStorage.setItem('knowledgeItemPageSize', String(itemPageSize));
    } catch {}
  }, [itemPageSize]);

  useEffect(() => {
    const sourceId = searchParams.get('source');
    const versionId = searchParams.get('version');
    const linkKey = sourceId ? `${sourceId}:${versionId || ''}` : '';
    if (!sourceId || handledSourceLink.current === linkKey || resolutions.length === 0) return;
    const source = resolutions.find(item => item.id === sourceId);
    if (!source) return;
    handledSourceLink.current = linkKey;
    setEditingResolution(source);
    setInitialVersionId(versionId);
    setIsEditorOpen(true);
  }, [resolutions, searchParams]);


  const fetchAll = async () => {
    setLoading(true);
    await Promise.all([fetchResolutions(), fetchForms(), fetchCategoryTree(), fetchOpTeams()]);
    setLoading(false);
  };

  const fetchResolutions = async () => {
    try {
      const res = await axiosInstance.get('/resolutions');
      setResolutions(res.data);
    } catch (error) {
      toast.error('Failed to fetch knowledge items');
    }
  };

  const fetchForms = async () => {
    try {
      const res = await axiosInstance.get('/forms');
      setForms(res.data);
    } catch (error) {
      console.error('Failed to fetch forms');
    }
  };

  const fetchCategoryTree = async () => {
    try {
      const res = await axiosInstance.get('/categories/tree');
      setCategoryTree(res.data.tree || []);
      setGeneralCategories(res.data.general_categories || []);
      setCategoryStats(res.data.stats || { sectors: 0, departments: 0, teams: 0, general_categories: 0 });
    } catch (error) {
      console.error('Failed to fetch category tree');
    }
  };

  const fetchOpTeams = async () => {
    try {
      const res = await axiosInstance.get('/teams');
      setOpTeams(res.data || []);
    } catch (error) {
      // Non-critical
    }
  };

  const fetchDataSourceInfo = async () => {
    try {
      const res = await axiosInstance.get('/batch-ingestion/config');
      setDataSourceInfo(res.data || null);
    } catch (error) {
      setDataSourceInfo(null);
    }
  };



  const fetchCustomSourceTypes = async () => {
    try {
      const res = await axiosInstance.get('/source-types');
      setCustomSourceTypes(res.data);
    } catch (error) {
      console.error('Failed to fetch custom source types');
    }
  };

  useEffect(() => {
    fetchCustomSourceTypes();
  }, []);

  // ============= SOURCE TYPE HANDLERS =============
  const handleAddCustomField = () => {
    if (!newCustomField.name || !newCustomField.label) {
      toast.error('Field name and label are required');
      return;
    }
    setSourceTypeForm(prev => ({
      ...prev,
      custom_fields: [...prev.custom_fields, { ...newCustomField }]
    }));
    setNewCustomField({ name: '', label: '', field_type: 'text', required: false, placeholder: '' });
  };

  const handleRemoveCustomField = (index) => {
    setSourceTypeForm(prev => ({
      ...prev,
      custom_fields: prev.custom_fields.filter((_, i) => i !== index)
    }));
  };

  // ========== Access Control (Admin RBAC) handlers ==========
  const isAdminUser = isTenantAdmin(user);
  const canManageLinkedSources = can(user, 'can_manage_knowledge');

  // Effective permission helpers for the current user
  const canEditFolder = (folderId) => {
    if (myPermissions.unrestricted) return true;
    const p = (myPermissions.folder_permissions || []).find(fp => fp.folder_id === folderId);
    return !!p?.can_edit;
  };
  const canDeleteFolder = (folderId) => {
    if (myPermissions.can_delete_unrestricted) return true;
    const p = (myPermissions.folder_permissions || []).find(fp => fp.folder_id === folderId);
    return !!p?.can_delete;
  };
  const canEditFile = (fileId, folderId) => {
    if (myPermissions.unrestricted) return true;
    const filePerm = (myPermissions.file_permissions || []).find(fp => fp.file_id === fileId);
    if (filePerm) return !!filePerm.can_edit; // file-level override
    return canEditFolder(folderId); // fall back to folder default
  };
  const canDeleteFile = (fileId, folderId) => {
    if (myPermissions.can_delete_unrestricted) return true;
    const filePerm = (myPermissions.file_permissions || []).find(fp => fp.file_id === fileId);
    if (filePerm) return !!filePerm.can_delete;
    return canDeleteFolder(folderId);
  };
  // Can the user upload/add manual entries? Unrestricted knowledge managers always. Otherwise requires edit on the selected team folder.
  const currentFolderId = selectedTeam?.id || null;
  const canAddToCurrentFolder = myPermissions.unrestricted || (currentFolderId && canEditFolder(currentFolderId));

  const fetchRbacUsers = async () => {
    try {
      const res = await axiosInstance.get('/rbac/users-for-permissions');
      setRbacUsers(res.data || []);
    } catch (e) {
      toast.error('Failed to load users');
    }
  };

  const loadPermissionsForUser = async (userId) => {
    if (!userId) {
      setRbacFolderPerms([]);
      setRbacFilePerms([]);
      return;
    }
    setRbacLoading(true);
    try {
      const [fp, filep] = await Promise.all([
        axiosInstance.get(`/rbac/folder-permissions?user_id=${userId}`),
        axiosInstance.get(`/rbac/file-permissions?user_id=${userId}`),
      ]);
      setRbacFolderPerms(fp.data || []);
      setRbacFilePerms(filep.data || []);
    } catch (e) {
      toast.error('Failed to load permissions');
    } finally {
      setRbacLoading(false);
    }
  };

  const openAccessControl = async () => {
    setAccessControlOpen(true);
    if (rbacUsers.length === 0) await fetchRbacUsers();
  };

  const grantFolderPermission = async (folderId, folderType, can_edit, can_delete) => {
    if (!rbacSelectedUserId) return toast.error('Select a user first');
    try {
      await axiosInstance.post('/rbac/folder-permissions', {
        folder_id: folderId,
        folder_type: folderType,
        user_id: rbacSelectedUserId,
        can_view: true,
        can_edit,
        can_delete,
      });
      toast.success('Folder permission saved');
      loadPermissionsForUser(rbacSelectedUserId);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save permission');
    }
  };

  const revokeFolderPermission = async (permId) => {
    try {
      await axiosInstance.delete(`/rbac/folder-permissions/${permId}`);
      toast.success('Permission revoked');
      loadPermissionsForUser(rbacSelectedUserId);
    } catch (e) {
      toast.error('Failed to revoke');
    }
  };

  const grantFilePermission = async (fileId, can_edit, can_delete) => {
    if (!rbacSelectedUserId) return toast.error('Select a user first');
    try {
      await axiosInstance.post('/rbac/file-permissions', {
        file_id: fileId,
        user_id: rbacSelectedUserId,
        can_view: true,
        can_edit,
        can_delete,
      });
      toast.success('File permission saved');
      loadPermissionsForUser(rbacSelectedUserId);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save permission');
    }
  };

  const revokeFilePermission = async (permId) => {
    try {
      await axiosInstance.delete(`/rbac/file-permissions/${permId}`);
      toast.success('Permission revoked');
      loadPermissionsForUser(rbacSelectedUserId);
    } catch (e) {
      toast.error('Failed to revoke');
    }
  };

  const handleSourceTypeSubmit = async (e) => {
    e.preventDefault();
    if (!sourceTypeForm.name) {
      toast.error('Source type name is required');
      return;
    }
    try {
      if (editingSourceType) {
        await axiosInstance.put(`/source-types/${editingSourceType.id}`, sourceTypeForm);
        toast.success('Source type updated');
      } else {
        await axiosInstance.post('/source-types', sourceTypeForm);
        toast.success('Source type created');
      }
      setIsSourceTypeDialogOpen(false);
      setEditingSourceType(null);
      setSourceTypeForm({ name: '', icon: '📄', color: 'gray', description: '', custom_fields: [] });
      fetchCustomSourceTypes();
    } catch (error) {
      toast.error('Failed to save source type');
    }
  };

  const handleDeleteSourceType = async (typeId) => {
    if (!window.confirm('Are you sure you want to delete this source type?')) return;
    try {
      await axiosInstance.delete(`/source-types/${typeId}`);
      toast.success('Source type deleted');
      fetchCustomSourceTypes();
    } catch (error) {
      toast.error('Failed to delete source type');
    }
  };

  const openEditSourceType = (type) => {
    setEditingSourceType(type);
    setSourceTypeForm({
      name: type.name,
      icon: type.icon || '📄',
      color: type.color || 'gray',
      description: type.description || '',
      custom_fields: type.custom_fields || []
    });
    setIsSourceTypeDialogOpen(true);
  };

  // ============= COMPUTED DATA =============
  const matchesSourceType = (item, sourceTypeId) => {
    const displayType = getSourceTypeDisplay(item.asset_type);
    if (sourceTypeId === 'resolution') {
      return item.asset_type === 'template' || item.asset_type === 'resolution' || displayType === 'resolution';
    }
    return item.asset_type === sourceTypeId || displayType === sourceTypeId;
  };

  const filteredResolutions = useMemo(() => {
    let items = resolutions;
    
    if (selectedGeneralCategory) {
      items = selectedGeneralCategory.id === '__unassigned__'
        ? items.filter(r => !r.team_id && !r.category_id)
        : items.filter(r => r.category_id === selectedGeneralCategory.id);
    } else if (selectedTeam) {
      items = items.filter(r => r.team_id === selectedTeam.id);
    }
    
    // Filter by source type if selected
    if (filterBySourceType) {
      items = items.filter(i => matchesSourceType(i, filterBySourceType));
    }
    
    if (searchQuery) {
      const q = searchQuery.toLowerCase().replace('#', '');
      items = items.filter(i => 
        i.id?.toLowerCase().includes(q) ||
        i.title?.toLowerCase().includes(q) ||
        i.keywords?.some(k => k.toLowerCase().includes(q))
      );
    }
    
    return items;
  }, [resolutions, selectedTeam, selectedGeneralCategory, searchQuery, filterBySourceType]);

  const visibleResolutions = useMemo(() => {
    const items = [...filteredResolutions];

    if (itemSort === 'name') {
      return items.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
    }

    if (itemSort === 'status') {
      return items.sort((a, b) => {
        const statusCompare = getIndexStatusMeta(a).sortRank - getIndexStatusMeta(b).sortRank;
        if (statusCompare !== 0) return statusCompare;
        return (a.title || '').localeCompare(b.title || '');
      });
    }

    return items.sort((a, b) => new Date(b.updated_at || 0) - new Date(a.updated_at || 0));
  }, [filteredResolutions, itemSort]);

  const stats = useMemo(() => {
    const notes = resolutions.filter(i => i.asset_type === 'note').length;
    const resolutionGuides = resolutions.filter(i => i.asset_type === 'template' || i.asset_type === 'resolution').length;
    const policies = resolutions.filter(i => i.asset_type === 'policy').length;
    const processes = resolutions.filter(i => i.asset_type === 'process').length;
    return { notes, resolutionGuides, policies, processes, total: resolutions.length };
  }, [resolutions]);

  const sourceTypeOptions = useMemo(() => {
    const builtIn = Object.values(SOURCE_TYPES).map(type => ({
      id: type.id,
      name: type.name,
      icon: type.icon,
      custom: false,
    }));
    const custom = customSourceTypes.map(type => ({
      id: `custom_${type.id}`,
      name: type.name,
      icon: type.icon,
      custom: true,
    }));
    const seen = new Set();
    return [...builtIn, ...custom].filter(type => {
      if (seen.has(type.id)) return false;
      seen.add(type.id);
      return true;
    });
  }, [customSourceTypes]);

  const activeSourceTypeOptions = useMemo(() => sourceTypeOptions.filter(type =>
    resolutions.some(item => matchesSourceType(item, type.id))
  ), [resolutions, sourceTypeOptions]);

  const itemPageCount = Math.max(1, Math.ceil(visibleResolutions.length / itemPageSize));
  const pagedResolutions = useMemo(() => {
    const start = (itemPage - 1) * itemPageSize;
    return visibleResolutions.slice(start, start + itemPageSize);
  }, [visibleResolutions, itemPage, itemPageSize]);
  const pageStart = visibleResolutions.length ? ((itemPage - 1) * itemPageSize) + 1 : 0;
  const pageEnd = Math.min(itemPage * itemPageSize, visibleResolutions.length);

  useEffect(() => {
    setItemPage(1);
  }, [selectedTeam?.id, selectedGeneralCategory?.id, searchQuery, filterBySourceType, itemSort, itemPageSize]);

  useEffect(() => {
    if (itemPage > itemPageCount) setItemPage(itemPageCount);
  }, [itemPage, itemPageCount]);

  const renderPagination = () => (
    <div className="flex flex-wrap items-center justify-between gap-3 text-xs text-gray-500" data-testid="knowledge-pagination">
      <span>{pageStart}–{pageEnd} of {visibleResolutions.length}</span>
      <div className="flex items-center gap-2">
        <button type="button" className="h-8 rounded-md border px-2 disabled:opacity-40" onClick={() => setItemPage(page => Math.max(1, page - 1))} disabled={itemPage <= 1} aria-label="Previous knowledge page">Previous</button>
        <span>Page {itemPage} of {itemPageCount}</span>
        <button type="button" className="h-8 rounded-md border px-2 disabled:opacity-40" onClick={() => setItemPage(page => Math.min(itemPageCount, page + 1))} disabled={itemPage >= itemPageCount} aria-label="Next knowledge page">Next</button>
      </div>
    </div>
  );

  // ============= GET ALL TEAMS FOR DROPDOWN =============
  const allTeams = useMemo(() => {
    const teams = [];
    categoryTree.forEach(sector => {
      sector.departments?.forEach(dept => {
        dept.teams?.forEach(team => {
          teams.push({
            ...team,
            fullPath: `${sector.name} → ${dept.name} → ${team.name}`,
            sectorName: sector.name,
            deptName: dept.name,
          });
        });
      });
    });
    return teams;
  }, [categoryTree]);

  // Doc count per knowledge_team — maps via operational team name
  const docCountByTeam = useMemo(() => {
    const opCounts = {};
    resolutions.forEach(r => { if (r.team_id) opCounts[r.team_id] = (opCounts[r.team_id] || 0) + 1; });
    const nameToCount = {};
    opTeams.forEach(ot => {
      const key = ot.name?.toLowerCase();
      if (key && opCounts[ot.id]) nameToCount[key] = (nameToCount[key] || 0) + opCounts[ot.id];
    });
    const result = { ...opCounts };
    allTeams.forEach(kt => {
      const key = kt.name?.toLowerCase();
      if (key && nameToCount[key] && !result[kt.id]) result[kt.id] = nameToCount[key];
    });
    return result;
  }, [resolutions, opTeams, allTeams]);

  const browseCategoryTree = useMemo(() => categoryTree
    .map(sector => ({
      ...sector,
      departments: (sector.departments || [])
        .map(department => ({
          ...department,
          teams: (department.teams || []).filter(team => (docCountByTeam[team.id] || 0) > 0),
        }))
        .filter(department => department.teams.length > 0),
    }))
    .filter(sector => sector.departments.length > 0), [categoryTree, docCountByTeam]);

  const categoryDocCounts = useMemo(() => {
    const counts = {};
    resolutions.forEach(item => {
      if (item.category_id) counts[item.category_id] = (counts[item.category_id] || 0) + 1;
    });
    return counts;
  }, [resolutions]);

  const browsableCategoryIds = useMemo(() => {
    const ids = new Set(Object.keys(categoryDocCounts));
    let changed = true;
    while (changed) {
      changed = false;
      generalCategories.forEach(category => {
        if (ids.has(category.id) && category.parent_id && !ids.has(category.parent_id)) {
          ids.add(category.parent_id);
          changed = true;
        }
      });
    }
    return ids;
  }, [categoryDocCounts, generalCategories]);

  const browsableGeneralCategories = useMemo(
    () => generalCategories.filter(category => browsableCategoryIds.has(category.id)),
    [generalCategories, browsableCategoryIds]
  );

  const unassignedDocCount = useMemo(
    () => resolutions.filter(item => !item.team_id && !item.category_id).length,
    [resolutions]
  );
  const categorizedDocCount = useMemo(() => resolutions.filter(item => item.category_id).length, [resolutions]);


  // ============= CATEGORY HANDLERS =============
  const handleSaveSector = async (e) => {
    e.preventDefault();
    try {
      if (editingCategory && editingCategoryType === 'sector') {
        await axiosInstance.put(`/categories/sectors/${editingCategory.id}`, sectorData);
        toast.success('Sector updated');
      } else {
        await axiosInstance.post('/categories/sectors', sectorData);
        toast.success('Sector created');
      }
      setIsSectorDialogOpen(false);
      setSectorData({ name: '', icon: '🏢', description: '' });
      setEditingCategory(null);
      setEditingCategoryType(null);
      fetchCategoryTree();
    } catch (error) {
      toast.error('Failed to save sector');
    }
  };

  const handleSaveGeneralCategory = async (e) => {
    e.preventDefault();
    try {
      if (editingCategory && editingCategoryType === 'general') {
        await axiosInstance.put(`/categories/general/${editingCategory.id}`, generalCategoryData);
        toast.success('Category updated');
      } else {
        await axiosInstance.post('/categories/general', generalCategoryData);
        toast.success('Category created');
      }
      setIsGeneralCategoryDialogOpen(false);
      setGeneralCategoryData({ name: '', icon: '🌐', description: '' });
      setEditingCategory(null);
      setEditingCategoryType(null);
      fetchCategoryTree();
    } catch (error) {
      toast.error('Failed to save category');
    }
  };

  const handleSaveDept = async (e) => {
    e.preventDefault();
    try {
      if (editingCategory && editingCategoryType === 'department') {
        await axiosInstance.put(`/categories/departments/${editingCategory.id}`, deptData);
        toast.success('Department updated');
      } else {
        await axiosInstance.post('/categories/departments', deptData);
        toast.success('Department created');
      }
      setIsDeptDialogOpen(false);
      setDeptData({ name: '', sector_id: '', icon: '💼', description: '' });
      setEditingCategory(null);
      setEditingCategoryType(null);
      fetchCategoryTree();
    } catch (error) {
      toast.error('Failed to save department');
    }
  };

  const handleSaveTeam = async (e) => {
    e.preventDefault();
    try {
      if (editingCategory && editingCategoryType === 'team') {
        await axiosInstance.put(`/categories/teams/${editingCategory.id}`, teamData);
        toast.success('Team updated');
      } else {
        await axiosInstance.post('/categories/teams', teamData);
        toast.success('Team created');
      }
      setIsTeamDialogOpen(false);
      setTeamData({ name: '', department_id: '', icon: '👥', description: '' });
      setEditingCategory(null);
      setEditingCategoryType(null);
      fetchCategoryTree();
    } catch (error) {
      toast.error('Failed to save team');
    }
  };

  const handleDeleteCategory = async (type, id) => {
    const confirmation = type === 'general'
      ? 'Delete this shared category? Its documents will become uncategorised and any child categories will move up one level.'
      : `Delete this ${type}? All child items will also be deleted.`;
    if (!window.confirm(confirmation)) return;
    try {
      const endpoint = type === 'general' ? `/categories/general/${id}` : `/categories/${type}s/${id}`;
      await axiosInstance.delete(endpoint);
      toast.success(`${type} deleted`);
      if (selectedTeam?.id === id) setSelectedTeam(null);
      if (selectedGeneralCategory?.id === id) setSelectedGeneralCategory(null);
      fetchCategoryTree();
      fetchResolutions();
    } catch (error) {
      toast.error(`Failed to delete ${type}`);
    }
  };

  const handleEditCategory = (type, item) => {
    setEditingCategory(item);
    setEditingCategoryType(type);
    if (type === 'sector') {
      setSectorData({ name: item.name, icon: item.icon, description: item.description || '' });
      setIsSectorDialogOpen(true);
    } else if (type === 'general') {
      setGeneralCategoryData({ name: item.name, icon: item.icon, description: item.description || '' });
      setIsGeneralCategoryDialogOpen(true);
    } else if (type === 'department') {
      setDeptData({ name: item.name, sector_id: item.sector_id, icon: item.icon, description: item.description || '' });
      setIsDeptDialogOpen(true);
    } else if (type === 'team') {
      setTeamData({ name: item.name, department_id: item.department_id, icon: item.icon, description: item.description || '' });
      setIsTeamDialogOpen(true);
    }
  };

  // ============= SOURCE HANDLERS =============
  const handleFormUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingForm(true);
    const uploadFormData = new FormData();
    uploadFormData.append('file', file);
    try {
      const res = await axiosInstance.post('/forms/upload', uploadFormData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Document uploaded');
      fetchForms();
      setSourceFormData({ ...sourceFormData, linked_forms: [...sourceFormData.linked_forms, res.data.id] });
    } catch (error) {
      toast.error('Upload failed');
    } finally {
      setUploadingForm(false);
    }
  };

  const handleTestLinkedFetch = async (url = sourceFormData.source_url) => {
    if (!url?.trim()) { toast.error('Enter a URL first'); return; }
    setTestingLinkedFetch(true);
    setLinkedFetchState(null);
    try {
      const response = await axiosInstance.post('/resolutions/linked-source/test-fetch', {
        source_url: url.trim(),
        extraction_scope: sourceFormData.extraction_scope || 'full_page',
        custom_selector: sourceFormData.extraction_scope === 'custom_selector' ? sourceFormData.custom_selector : null,
        source_auth_method: sourceFormData.source_auth_method || 'public',
        source_auth: sourceFormData.source_auth_method === 'public' ? null : sourceFormData.source_auth,
      });
      setLinkedFetchState({ success: true, ...response.data });
      toast.success(`Fetch succeeded (${response.data.content_type})`);
    } catch (error) {
      const message = error.response?.data?.detail || error.message || 'Fetch failed';
      setLinkedFetchState({ success: false, message });
      toast.error(message);
    } finally {
      setTestingLinkedFetch(false);
    }
  };

  const handleSourceSubmit = async (e) => {
    e.preventDefault();

    if (!sourceFormData.document_type.trim()) {
      toast.error('Enter a document type before saving.');
      return;
    }
    if (
      sourceFormData.visibility === 'restricted'
      && !sourceFormData.allowed_roles.trim()
      && !sourceFormData.allowed_teams.trim()
    ) {
      toast.error('Choose at least one allowed role or team for restricted access.');
      return;
    }
    if (sourceFormData.source_type === 'linked' && !linkedFetchState?.success) {
      toast.error('Test and preview the document before saving it.');
      return;
    }
    
    // Map asset type for backend compatibility
    let assetType = selectedSourceType;
    if (assetType === 'resolution') assetType = 'template'; // Keep template for backend compatibility
    
    const payload = {
      title: sourceFormData.title,
      keywords: sourceFormData.keywords ? sourceFormData.keywords.split(',').map(k => k.trim()).filter(k => k) : [],
      description: sourceFormData.description,
      resolution_steps: (sourceFormData.resolution_steps || sourceFormData.process_steps || sourceFormData.examples)
        ? (sourceFormData.resolution_steps || sourceFormData.process_steps || sourceFormData.examples).split('\n').filter(s => s.trim())
        : [],
      email_template: sourceFormData.email_template || null,
      contacts: sourceFormData.contacts ? sourceFormData.contacts.split(',').map(c => c.trim()) : [],
      ticket_reference: sourceFormData.ticket_reference || null,
      sla_hours: sourceFormData.sla_hours ? parseFloat(sourceFormData.sla_hours) : null,
      sla_details: sourceFormData.sla_details || null,
      linked_docs: sourceFormData.linked_docs || null,
      linked_forms: sourceFormData.linked_forms,
      team_id: sourceFormData.team_id || (selectedTeam?.id) || null,
      category_id: sourceFormData.category_id || null,
      asset_type: assetType,
      source_type_name: sourceFormData.document_type.trim() || null,
      // Additional fields for Policy and Process
      policy_content: sourceFormData.policy_content || null,
      effective_date: sourceFormData.effective_date || null,
      version: sourceFormData.version || null,
      owner: sourceFormData.owner || sourceFormData.process_owner || null,
      review_date: sourceFormData.review_date || null,
      frequency: sourceFormData.frequency || null,
      tools_required: sourceFormData.tools_required || null,
      custom_fields: sourceFormData.custom_fields || [],
      source_type: sourceFormData.source_type || 'static',
      source_url: sourceFormData.source_type === 'linked' ? sourceFormData.source_url : null,
      refresh_interval: sourceFormData.source_type === 'linked' ? sourceFormData.refresh_interval : 'manual_check_only',
      extraction_scope: sourceFormData.source_type === 'linked' ? (sourceFormData.extraction_scope || 'full_page') : 'full_page',
      custom_selector: sourceFormData.source_type === 'linked' && sourceFormData.extraction_scope === 'custom_selector' ? sourceFormData.custom_selector : null,
      source_auth_method: sourceFormData.source_type === 'linked' ? (sourceFormData.source_auth_method || 'public') : 'public',
      source_auth: sourceFormData.source_type === 'linked' && sourceFormData.source_auth_method !== 'public' ? sourceFormData.source_auth : null,
      preview_content_hash: sourceFormData.source_type === 'linked' ? linkedFetchState?.content_hash || null : null,
      badge_ids: sourceFormData.badge_ids || [],
      visibility: sourceFormData.visibility || 'public',
      allowed_roles: sourceFormData.visibility === 'restricted'
        ? sourceFormData.allowed_roles.split(',').map(value => value.trim()).filter(Boolean)
        : [],
      allowed_teams: sourceFormData.visibility === 'restricted'
        ? sourceFormData.allowed_teams.split(',').map(value => value.trim()).filter(Boolean)
        : [],
      source_published_at: sourceFormData.source_published_at || null,
      effective_from: sourceFormData.effective_from || null,
      effective_until: sourceFormData.effective_until || null,
      last_reviewed_at: sourceFormData.last_reviewed_at || null,
      version_label: sourceFormData.version_label || null,
      review_schedule: reviewSchedulePayload(sourceFormData.review_schedule),
    };

    try {
      if (editingResolution) {
        await axiosInstance.put(`/resolutions/${editingResolution.id}`, payload);
        toast.success('Source updated');
      } else {
        await axiosInstance.post('/resolutions', payload);
        toast.success('Source created');
      }
      setIsSourceDialogOpen(false);
      resetSourceForm();
      fetchResolutions();
      fetchCategoryTree();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save source');
    }
  };

  const handleAutoExtract = async () => {
    if (!rawInput.trim()) {
      toast.error('Please enter some text');
      return;
    }
    setExtracting(true);
    try {
      const res = await axiosInstance.post('/resolutions/auto-extract', { raw_text: rawInput });
      const extracted = res.data;
      setSourceFormData({
        ...sourceFormData,
        title: extracted.title || '',
        keywords: extracted.keywords ? extracted.keywords.join(', ') : '',
        description: extracted.description || '',
        resolution_steps: extracted.resolution_steps ? extracted.resolution_steps.join('\n') : '',
        email_template: extracted.email_template || '',
        contacts: extracted.contacts ? extracted.contacts.join(', ') : '',
        ticket_reference: extracted.ticket_reference || '',
        sla_hours: extracted.sla_hours || '',
        sla_details: extracted.sla_details || '',
      });
      setShowAutoExtract(false);
      setRawInput('');
      toast.success('Fields extracted!');
    } catch (error) {
      toast.error('Extraction failed');
    } finally {
      setExtracting(false);
    }
  };

  const handleEdit = (item) => {
    setSelectedKnowledgeItemId(null);
    setEditingResolution(item);
    setIsEditorOpen(true);
  };

  const handleReindex = async (item) => {
    try {
      await axiosInstance.post(`/resolutions/${item.id}/reindex`);
      toast.success(`Re-indexing "${item.title}" — refresh in a few seconds to see status update`);
      // Optimistically mark as indexing in UI
      setResolutions(prev => prev.map(r => r.id === item.id ? { ...r, indexing_status: 'indexing' } : r));
      // Refetch shortly to pick up the completed status
      setTimeout(() => fetchResolutions(), 6000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to start re-indexing');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this source?')) return;
    try {
      await axiosInstance.delete(`/resolutions/${id}`);
      toast.success('Deleted');
      setSelectedKnowledgeItemId(null);
      fetchResolutions();
      fetchCategoryTree();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Delete failed';
      toast.error(msg);
    }
  };

  const handleToggleProtection = async (id, currentProtected) => {
    try {
      await axiosInstance.patch(`/resolutions/${id}/protect`, null, { params: { is_protected: !currentProtected } });
      toast.success(currentProtected ? 'Protection removed' : 'Document protected');
      fetchResolutions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update protection');
    }
  };

  const resetSourceForm = () => {
    setSourceFormData({
      title: '', keywords: '', description: '', examples: '', resolution_steps: '',
      email_template: '', contacts: '', ticket_reference: '', sla_hours: '', sla_details: '',
      policy_content: '', effective_date: '', version: '', owner: '', review_date: '',
      process_steps: '', process_owner: '', department: '', frequency: '', tools_required: '',
      linked_docs: '', linked_forms: [], team_id: selectedTeam?.id || null, custom_fields: [],
      source_type: 'static', source_url: '', refresh_interval: 'manual_check_only',
      extraction_scope: 'full_page', custom_selector: '',
      source_auth_method: 'public', source_auth: {},
      document_type: '',
      category_id: selectedGeneralCategory?.id && selectedGeneralCategory.id !== '__unassigned__' ? selectedGeneralCategory.id : null,
      badge_ids: [],
      visibility: 'public', allowed_roles: '', allowed_teams: '',
      source_published_at: '', effective_from: '', effective_until: '',
      last_reviewed_at: '', version_label: '', review_schedule: EMPTY_REVIEW_SCHEDULE,
    });
    setLinkedFetchState(null);
    setEditingResolution(null);
    setSelectedSourceType('note');
    setShowAutoExtract(false);
    setRawInput('');
    setSourceTeamPickerOpen(false);
    setSourceCategoryPickerOpen(false);
    setSourceDatesOpen(false);
  };

  const openSourceDialog = (sourceType = 'static') => {
    resetSourceForm();
    setSourceFormData(prev => ({ ...prev, source_type: sourceType }));
    setIsSourceDialogOpen(true);
  };

  // Add custom field
  const addCustomField = () => {
    setSourceFormData({
      ...sourceFormData,
      custom_fields: [...sourceFormData.custom_fields, { name: '', value: '' }]
    });
  };

  const updateCustomField = (index, field, value) => {
    const updated = [...sourceFormData.custom_fields];
    updated[index] = { ...updated[index], [field]: value };
    setSourceFormData({ ...sourceFormData, custom_fields: updated });
  };

  const removeCustomField = (index) => {
    setSourceFormData({
      ...sourceFormData,
      custom_fields: sourceFormData.custom_fields.filter((_, i) => i !== index)
    });
  };

  // ============= RENDER FUNCTIONS =============
  const getShortId = (id) => {
    if (!id) return 'N/A';
    return id.substring(0, 8).toUpperCase();
  };

  const getTeamInfo = (teamId) => {
    if (!teamId) return null;
    const generalCat = generalCategories.find(gc => gc.id === teamId);
    if (generalCat) {
      return { ...generalCat, fullPath: `${generalCat.name} (Available to all)`, isGeneral: true };
    }
    return allTeams.find(t => t.id === teamId);
  };

  // Merge built-in and custom source types for the selector
  const allSourceTypes = useMemo(() => {
    const builtIn = Object.values(SOURCE_TYPES);
    const custom = customSourceTypes.map(t => ({
      id: `custom_${t.id}`,
      name: t.name,
      icon: t.icon,
      color: t.color || 'gray',
      description: t.description || 'Custom source type',
      fields: ['title', 'description', 'team_id', ...(t.custom_fields?.map(f => f.name) || [])],
      requiredFields: ['title', 'description'],
      isCustom: true,
      customFields: t.custom_fields || []
    }));
    return [...builtIn, ...custom];
  }, [customSourceTypes]);

  const renderDynamicFormFields = () => {
    const typeConfig = SOURCE_TYPES[selectedSourceType] || allSourceTypes.find(t => t.id === selectedSourceType);
    const isCustomType = selectedSourceType.startsWith('custom_');
    const customTypeConfig = isCustomType ? allSourceTypes.find(t => t.id === selectedSourceType) : null;
    
    return (
      <div className="space-y-4">
        {/* Common Fields */}
        <div>
          <Label>Title <span className="text-red-500">*</span></Label>
          <Input 
            value={sourceFormData.title} 
            onChange={(e) => setSourceFormData({ ...sourceFormData, title: e.target.value })} 
            required 
            className="mt-1"
            placeholder={`Enter ${typeConfig?.name?.toLowerCase() || 'source'} title...`}
          />
        </div>

        <div>
          <ExpandableTextarea 
            label={<>Description <span className="text-red-500">*</span></>}
            value={sourceFormData.description} 
            onChange={(e) => setSourceFormData({ ...sourceFormData, description: e.target.value })} 
            minRows={3}
            maxRows={15}
            required 
            placeholder="Enter detailed description..."
          />
        </div>

        {/* Custom Type Fields */}
        {isCustomType && customTypeConfig?.customFields?.map((field, idx) => (
          <div key={idx}>
            <Label>{field.label} {field.required && <span className="text-red-500">*</span>}</Label>
            {field.field_type === 'textarea' ? (
              <ExpandableTextarea
                value={sourceFormData.custom_fields?.find(f => f.name === field.name)?.value || ''}
                onChange={(e) => {
                  const existingFields = sourceFormData.custom_fields || [];
                  const fieldIdx = existingFields.findIndex(f => f.name === field.name);
                  if (fieldIdx >= 0) {
                    existingFields[fieldIdx].value = e.target.value;
                  } else {
                    existingFields.push({ name: field.name, value: e.target.value });
                  }
                  setSourceFormData({ ...sourceFormData, custom_fields: [...existingFields] });
                }}
                minRows={3}
                maxRows={10}
                required={field.required}
                placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}...`}
              />
            ) : field.field_type === 'number' ? (
              <Input
                type="number"
                value={sourceFormData.custom_fields?.find(f => f.name === field.name)?.value || ''}
                onChange={(e) => {
                  const existingFields = sourceFormData.custom_fields || [];
                  const fieldIdx = existingFields.findIndex(f => f.name === field.name);
                  if (fieldIdx >= 0) {
                    existingFields[fieldIdx].value = e.target.value;
                  } else {
                    existingFields.push({ name: field.name, value: e.target.value });
                  }
                  setSourceFormData({ ...sourceFormData, custom_fields: [...existingFields] });
                }}
                required={field.required}
                placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}...`}
                className="mt-1"
              />
            ) : field.field_type === 'date' ? (
              <Input
                type="date"
                value={sourceFormData.custom_fields?.find(f => f.name === field.name)?.value || ''}
                onChange={(e) => {
                  const existingFields = sourceFormData.custom_fields || [];
                  const fieldIdx = existingFields.findIndex(f => f.name === field.name);
                  if (fieldIdx >= 0) {
                    existingFields[fieldIdx].value = e.target.value;
                  } else {
                    existingFields.push({ name: field.name, value: e.target.value });
                  }
                  setSourceFormData({ ...sourceFormData, custom_fields: [...existingFields] });
                }}
                required={field.required}
                className="mt-1"
              />
            ) : (
              <Input
                value={sourceFormData.custom_fields?.find(f => f.name === field.name)?.value || ''}
                onChange={(e) => {
                  const existingFields = sourceFormData.custom_fields || [];
                  const fieldIdx = existingFields.findIndex(f => f.name === field.name);
                  if (fieldIdx >= 0) {
                    existingFields[fieldIdx].value = e.target.value;
                  } else {
                    existingFields.push({ name: field.name, value: e.target.value });
                  }
                  setSourceFormData({ ...sourceFormData, custom_fields: [...existingFields] });
                }}
                required={field.required}
                placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}...`}
                className="mt-1"
              />
            )}
          </div>
        ))}

        {/* Keywords - for Resolution, Policy, Process */}
        {['resolution', 'policy', 'process'].includes(selectedSourceType) && (
          <div>
            <Label>Keywords</Label>
            <Input 
              value={sourceFormData.keywords} 
              onChange={(e) => setSourceFormData({ ...sourceFormData, keywords: e.target.value })} 
              placeholder="comma-separated keywords"
              className="mt-1"
            />
          </div>
        )}

        <KnowledgeBadgePicker
          value={sourceFormData.badge_ids || []}
          onChange={(badgeIds) => setSourceFormData({ ...sourceFormData, badge_ids: badgeIds })}
          user={user}
          compact
        />

        {renderTeamSelector()}

        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
          <p className="mb-2 text-xs font-medium text-gray-700">Document dates and review (optional)</p>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
            <div>
              <Label className="text-xs">Published</Label>
              <Input type="date" value={sourceFormData.source_published_at || ''} onChange={(e) => setSourceFormData({ ...sourceFormData, source_published_at: e.target.value })} className="mt-1 bg-white" />
            </div>
            <div>
              <Label className="text-xs">Effective from</Label>
              <Input type="date" value={sourceFormData.effective_from || ''} onChange={(e) => setSourceFormData({ ...sourceFormData, effective_from: e.target.value })} className="mt-1 bg-white" />
            </div>
            <div>
              <Label className="text-xs">Effective until</Label>
              <Input type="date" value={sourceFormData.effective_until || ''} onChange={(e) => setSourceFormData({ ...sourceFormData, effective_until: e.target.value })} className="mt-1 bg-white" />
            </div>
            <div>
              <Label className="text-xs">Last reviewed</Label>
              <Input type="date" value={sourceFormData.last_reviewed_at || ''} onChange={(e) => setSourceFormData({ ...sourceFormData, last_reviewed_at: e.target.value })} className="mt-1 bg-white" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Version label</Label>
              <Input value={sourceFormData.version_label || ''} onChange={(e) => setSourceFormData({ ...sourceFormData, version_label: e.target.value })} placeholder="e.g. 2026.2" className="mt-1 bg-white" />
            </div>
            <ReviewScheduleFields value={sourceFormData.review_schedule || EMPTY_REVIEW_SCHEDULE} onChange={(next) => setSourceFormData((previous) => ({ ...previous, review_schedule: next }))} anchorDate={sourceFormData.last_reviewed_at || sourceFormData.source_published_at || sourceFormData.effective_from} teamName={allTeams.find((team) => team.id === sourceFormData.team_id)?.name} />
          </div>
        </div>

        {/* Resolution-specific fields */}
        {selectedSourceType === 'resolution' && (
          <>
            <div>
              <ExpandableTextarea 
                label={<>Resolution Steps <span className="text-red-500">*</span></>}
                value={sourceFormData.resolution_steps} 
                onChange={(e) => setSourceFormData({ ...sourceFormData, resolution_steps: e.target.value })} 
                minRows={4}
                maxRows={20}
                required 
                placeholder="One step per line..."
              />
            </div>
            <div>
              <ExpandableTextarea 
                label="Email Template"
                value={sourceFormData.email_template} 
                onChange={(e) => setSourceFormData({ ...sourceFormData, email_template: e.target.value })} 
                minRows={3}
                maxRows={15}
                placeholder="Optional email template..."
                className="font-mono text-sm"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Contacts</Label>
                <Input value={sourceFormData.contacts} onChange={(e) => setSourceFormData({ ...sourceFormData, contacts: e.target.value })} className="mt-1" placeholder="comma-separated" />
              </div>
              <div>
                <Label>Ticket Reference</Label>
                <Input value={sourceFormData.ticket_reference} onChange={(e) => setSourceFormData({ ...sourceFormData, ticket_reference: e.target.value })} className="mt-1" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>SLA Hours</Label>
                <Input type="number" value={sourceFormData.sla_hours} onChange={(e) => setSourceFormData({ ...sourceFormData, sla_hours: e.target.value })} className="mt-1" />
              </div>
              <div>
                <Label>SLA Details</Label>
                <Input value={sourceFormData.sla_details} onChange={(e) => setSourceFormData({ ...sourceFormData, sla_details: e.target.value })} className="mt-1" />
              </div>
            </div>
          </>
        )}

        {/* Policy-specific fields */}
        {selectedSourceType === 'policy' && (
          <>
            <div>
              <ExpandableTextarea 
                label={<>Policy Content <span className="text-red-500">*</span></>}
                value={sourceFormData.policy_content} 
                onChange={(e) => setSourceFormData({ ...sourceFormData, policy_content: e.target.value })} 
                minRows={6}
                maxRows={25}
                required 
                placeholder="Enter the full policy content..."
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Effective Date</Label>
                <Input type="date" value={sourceFormData.effective_date} onChange={(e) => setSourceFormData({ ...sourceFormData, effective_date: e.target.value })} className="mt-1" />
              </div>
              <div>
                <Label>Version</Label>
                <Input value={sourceFormData.version} onChange={(e) => setSourceFormData({ ...sourceFormData, version: e.target.value })} className="mt-1" placeholder="e.g., 1.0" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Policy Owner</Label>
                <Input value={sourceFormData.owner} onChange={(e) => setSourceFormData({ ...sourceFormData, owner: e.target.value })} className="mt-1" />
              </div>
              <div>
                <Label>Review Date</Label>
                <Input type="date" value={sourceFormData.review_date} onChange={(e) => setSourceFormData({ ...sourceFormData, review_date: e.target.value })} className="mt-1" />
              </div>
            </div>
          </>
        )}

        {/* Process-specific fields */}
        {selectedSourceType === 'process' && (
          <>
            <div>
              <ExpandableTextarea 
                label={<>Process Steps <span className="text-red-500">*</span></>}
                value={sourceFormData.process_steps} 
                onChange={(e) => setSourceFormData({ ...sourceFormData, process_steps: e.target.value })} 
                minRows={5}
                maxRows={25}
                required 
                placeholder="One step per line..."
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Process Owner</Label>
                <Input value={sourceFormData.process_owner} onChange={(e) => setSourceFormData({ ...sourceFormData, process_owner: e.target.value })} className="mt-1" />
              </div>
              <div>
                <Label>Department</Label>
                <Input value={sourceFormData.department} onChange={(e) => setSourceFormData({ ...sourceFormData, department: e.target.value })} className="mt-1" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Frequency</Label>
                <Input value={sourceFormData.frequency} onChange={(e) => setSourceFormData({ ...sourceFormData, frequency: e.target.value })} className="mt-1" placeholder="e.g., Daily, Weekly" />
              </div>
              <div>
                <Label>Tools Required</Label>
                <Input value={sourceFormData.tools_required} onChange={(e) => setSourceFormData({ ...sourceFormData, tools_required: e.target.value })} className="mt-1" placeholder="comma-separated" />
              </div>
            </div>
          </>
        )}

        {/* Custom Fields */}
        <div className="border-t pt-4">
          <div className="flex items-center justify-between mb-3">
            <Label className="text-sm font-medium">Custom Fields</Label>
            <Button type="button" variant="outline" size="sm" onClick={addCustomField}>
              <Plus size={14} className="mr-1" /> Add Field
            </Button>
          </div>
          {sourceFormData.custom_fields.map((field, idx) => (
            <div key={idx} className="flex gap-2 mb-2">
              <Input 
                placeholder="Field name" 
                value={field.name} 
                onChange={(e) => updateCustomField(idx, 'name', e.target.value)}
                className="w-1/3"
              />
              <Input 
                placeholder="Field value" 
                value={field.value} 
                onChange={(e) => updateCustomField(idx, 'value', e.target.value)}
                className="flex-1"
              />
              <Button type="button" variant="ghost" size="sm" onClick={() => removeCustomField(idx)}>
                <X size={14} className="text-red-500" />
              </Button>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderTeamSelector = () => {
    const selectedTeamInfo = allTeams.find(t => t.id === sourceFormData.team_id);
    const selectedGeneralCat = generalCategories.find(gc => gc.id === sourceFormData.team_id);
    
    // Group teams by sector and department
    const groupedTeams = {};
    categoryTree.forEach(sector => {
      sector.departments?.forEach(dept => {
        const groupKey = `${sector.icon} ${sector.name} / ${dept.icon} ${dept.name}`;
        groupedTeams[groupKey] = dept.teams || [];
      });
    });

    return (
      <div className="rounded-lg border border-indigo-100 bg-indigo-50/40 p-3">
        <div className="mb-2 flex items-center gap-2">
          <Label className="flex items-center gap-2"><Users size={14} /> Access and ownership</Label>
          <InfoHint label="About access and ownership">Assign a registered team, then optionally place the document in a shared category.</InfoHint>
        </div>
        <select
          className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          value={sourceFormData.team_id || ''}
          onChange={(e) => setSourceFormData({ ...sourceFormData, team_id: e.target.value || null })}
        >
          <option value="">-- Select a Category --</option>
          {generalCategories.length > 0 && (
            <optgroup label="🌐 General">
              {generalCategories.map(gc => (
                <option key={gc.id} value={gc.id}>{gc.icon} {gc.name}</option>
              ))}
            </optgroup>
          )}
          {Object.entries(groupedTeams).map(([groupName, teams]) => (
            teams.length > 0 && (
              <optgroup key={groupName} label={groupName}>
                {teams.map(team => (
                  <option key={team.id} value={team.id}>{team.icon} {team.name}</option>
                ))}
              </optgroup>
            )
          ))}
        </select>
        {selectedGeneralCat && (
          <div className="mt-2 px-2 py-1 bg-blue-50 border border-blue-200 rounded text-xs text-blue-700">
            <IconFromString name={selectedGeneralCat.icon} size={12} className="inline-block mr-1" />Assigned to: {selectedGeneralCat.name} (Available to all teams)
          </div>
        )}
        {selectedTeamInfo && (
          <div className="mt-2 px-2 py-1 bg-green-50 border border-green-200 rounded text-xs text-green-700">
            ✓ {selectedTeamInfo.fullPath}
          </div>
        )}
      </div>
    );
  };

  const renderSourceMetadataFields = () => {
    const selectedTeamInfo = allTeams.find(team => team.id === sourceFormData.team_id);
    const selectedCategory = generalCategories.find(category => category.id === sourceFormData.category_id);
    const categoryById = new Map(generalCategories.map(category => [category.id, category]));
    const categoryLabel = (category) => {
      const labels = [category.name];
      let parentId = category.parent_id;
      let guard = 0;
      while (parentId && categoryById.has(parentId) && guard < 6) {
        const parent = categoryById.get(parentId);
        labels.unshift(parent.name);
        parentId = parent.parent_id;
        guard += 1;
      }
      return labels.join(' / ');
    };
    const hasDates = Boolean(
      sourceFormData.source_published_at
      || sourceFormData.effective_from
      || sourceFormData.effective_until
      || sourceFormData.last_reviewed_at
      || sourceFormData.version_label
    );

    return (
      <div className="space-y-5" data-testid="source-metadata-fields">
        <div>
          <Label>Title <span className="text-red-500">*</span></Label>
          <Input
            value={sourceFormData.title}
            onChange={(event) => setSourceFormData(previous => ({ ...previous, title: event.target.value }))}
            required
            className="mt-1"
            placeholder="Enter a clear source title"
            data-testid="source-title"
          />
        </div>

        <div>
          <ExpandableTextarea
            label={<>Description <span className="text-red-500">*</span></>}
            value={sourceFormData.description}
            onChange={(event) => setSourceFormData(previous => ({ ...previous, description: event.target.value }))}
            minRows={3}
            maxRows={15}
            required
            placeholder="Describe what this source covers"
            data-testid="source-description"
          />
        </div>

        <section className="border-t border-slate-200 pt-4" aria-labelledby="source-classification-heading">
          <h3 id="source-classification-heading" className="text-sm font-semibold text-slate-900">Classification</h3>
          <p className="mt-0.5 text-xs text-slate-500">Describe this source without forcing it into a fixed list.</p>
          <div className="mt-3 grid gap-4 md:grid-cols-2">
            <div>
              <Label className="text-xs">Document type</Label>
              <Input
                value={sourceFormData.document_type}
                onChange={(event) => setSourceFormData(previous => ({ ...previous, document_type: event.target.value }))}
                placeholder="e.g. Process guide"
                className="mt-1 bg-white"
                required
                data-testid="source-document-type"
              />
            </div>
            <KnowledgeBadgePicker
              value={sourceFormData.badge_ids || []}
              onChange={(badgeIds) => setSourceFormData(previous => ({ ...previous, badge_ids: badgeIds }))}
              user={user}
              compact
              inlineCreate
            />
          </div>
        </section>

        <section aria-labelledby="source-ownership-heading">
          <div className="mb-2 flex items-center gap-2">
            <h3 id="source-ownership-heading" className="text-sm font-semibold text-slate-900">Access and ownership</h3>
            <InfoHint label="About access and ownership">Assign a registered team, then optionally place the source in a shared category.</InfoHint>
          </div>
          <div className="overflow-hidden rounded-xl bg-slate-50">
            <div className="grid grid-cols-[40px_1fr_auto] items-center gap-3 px-3 py-3">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-sky-100 text-sky-700"><Users size={17} /></span>
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-900">Team</p>
                <p className="truncate text-xs text-slate-500">{selectedTeamInfo?.fullPath || selectedTeamInfo?.name || 'No team assigned'}</p>
              </div>
              <Button type="button" variant="outline" size="sm" onClick={() => setSourceTeamPickerOpen(open => !open)} aria-expanded={sourceTeamPickerOpen}>Assign</Button>
            </div>
            {sourceTeamPickerOpen && (
              <div className="border-t border-slate-200 px-3 py-3">
                <Label className="text-xs" htmlFor="source-team-assignment">Registered team structure</Label>
                <select
                  id="source-team-assignment"
                  value={sourceFormData.team_id || ''}
                  onChange={(event) => setSourceFormData(previous => ({ ...previous, team_id: event.target.value || null }))}
                  className="mt-1 h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm"
                  data-testid="source-team-assignment"
                >
                  <option value="">Keep unassigned</option>
                  {[...allTeams]
                    .sort((first, second) => (first.fullPath || first.name).localeCompare(second.fullPath || second.name))
                    .map(team => <option key={team.id} value={team.id}>{team.fullPath || team.name}</option>)}
                </select>
              </div>
            )}

            <div className="grid grid-cols-[40px_1fr_auto] items-center gap-3 border-t border-slate-200 px-3 py-3">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-sky-100 text-sky-700"><FolderOpen size={17} /></span>
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-900">Category <span className="font-normal text-slate-400">Optional</span></p>
                <p className="truncate text-xs text-slate-500">{selectedCategory ? categoryLabel(selectedCategory) : 'No category assigned'}</p>
              </div>
              <Button type="button" variant="outline" size="sm" onClick={() => setSourceCategoryPickerOpen(open => !open)} aria-expanded={sourceCategoryPickerOpen}>Assign</Button>
            </div>
            {sourceCategoryPickerOpen && (
              <div className="border-t border-slate-200 px-3 py-3">
                <Label className="text-xs" htmlFor="source-category-assignment">Shared category</Label>
                <select
                  id="source-category-assignment"
                  value={sourceFormData.category_id || ''}
                  onChange={(event) => setSourceFormData(previous => ({ ...previous, category_id: event.target.value || null }))}
                  className="mt-1 h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm"
                  data-testid="source-category-assignment"
                >
                  <option value="">No category assigned</option>
                  {[...generalCategories]
                    .sort((first, second) => categoryLabel(first).localeCompare(categoryLabel(second)))
                    .map(category => <option key={category.id} value={category.id}>{categoryLabel(category)}</option>)}
                </select>
              </div>
            )}
          </div>
        </section>

        <section aria-labelledby="source-access-heading">
          <div className="mb-2 flex items-center gap-2">
            <Shield size={15} className="text-sky-600" />
            <div>
              <h3 id="source-access-heading" className="text-sm font-semibold text-slate-900">Access control</h3>
              <p className="text-xs text-slate-500">Choose who can view this source.</p>
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2" data-testid="source-visibility-control">
            <button
              type="button"
              onClick={() => setSourceFormData(previous => ({ ...previous, visibility: 'public' }))}
              className={`rounded-xl border p-4 text-left ${sourceFormData.visibility === 'public' ? 'border-sky-500 bg-sky-50' : 'border-slate-200 bg-white'}`}
              aria-pressed={sourceFormData.visibility === 'public'}
              data-testid="source-visibility-public"
            >
              <span className="text-sm font-semibold text-slate-900">Public</span>
              <span className="mt-1 block text-xs text-slate-500">Visible to everyone in this organisation</span>
            </button>
            <button
              type="button"
              onClick={() => setSourceFormData(previous => ({ ...previous, visibility: 'restricted' }))}
              className={`rounded-xl border p-4 text-left ${sourceFormData.visibility === 'restricted' ? 'border-sky-500 bg-sky-50' : 'border-slate-200 bg-white'}`}
              aria-pressed={sourceFormData.visibility === 'restricted'}
              data-testid="source-visibility-restricted"
            >
              <span className="text-sm font-semibold text-slate-900">Restricted</span>
              <span className="mt-1 block text-xs text-slate-500">Only approved roles and teams</span>
            </button>
          </div>
          {sourceFormData.visibility === 'restricted' && (
            <div className="mt-3 grid gap-3 rounded-xl bg-slate-50 p-3 sm:grid-cols-2">
              <div>
                <Label className="text-xs">Allowed Roles <span className="font-normal text-slate-400">Comma-separated</span></Label>
                <Input
                  value={sourceFormData.allowed_roles}
                  onChange={(event) => setSourceFormData(previous => ({ ...previous, allowed_roles: event.target.value }))}
                  placeholder="employee, team_manager"
                  className="mt-1 bg-white"
                  data-testid="source-allowed-roles"
                />
              </div>
              <div>
                <Label className="text-xs">Allowed Teams <span className="font-normal text-slate-400">Comma-separated team IDs</span></Label>
                <Input
                  value={sourceFormData.allowed_teams}
                  onChange={(event) => setSourceFormData(previous => ({ ...previous, allowed_teams: event.target.value }))}
                  placeholder="team-id-1, team-id-2"
                  className="mt-1 bg-white"
                  data-testid="source-allowed-teams"
                />
              </div>
            </div>
          )}
        </section>

        <section className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          <button
            type="button"
            onClick={() => setSourceDatesOpen(open => !open)}
            aria-expanded={sourceDatesOpen}
            className="flex w-full items-center justify-between gap-3 px-3 py-2.5 text-left"
            data-testid="source-dates-toggle"
          >
            <span className="text-sm font-medium text-slate-800">Document dates and review <span className="font-normal text-slate-400">Optional · {reviewSchedulePayload(sourceFormData.review_schedule) ? 'Review reminders on' : hasDates ? 'Configured' : 'Not set'}</span></span>
            <ChevronDown size={15} className={`shrink-0 text-slate-400 transition-transform ${sourceDatesOpen ? 'rotate-180' : ''}`} />
          </button>
          {sourceDatesOpen && (
            <div className="grid gap-3 border-t border-slate-200 bg-slate-50 p-3 sm:grid-cols-2 lg:grid-cols-3">
              <div><Label className="text-xs">Published</Label><Input type="date" value={sourceFormData.source_published_at || ''} onChange={(event) => setSourceFormData(previous => ({ ...previous, source_published_at: event.target.value }))} className="mt-1 bg-white" /></div>
              <div><Label className="text-xs">Effective from</Label><Input type="date" value={sourceFormData.effective_from || ''} onChange={(event) => setSourceFormData(previous => ({ ...previous, effective_from: event.target.value }))} className="mt-1 bg-white" /></div>
              <div><Label className="text-xs">Effective until</Label><Input type="date" value={sourceFormData.effective_until || ''} onChange={(event) => setSourceFormData(previous => ({ ...previous, effective_until: event.target.value }))} className="mt-1 bg-white" /></div>
              <div><Label className="text-xs">Last reviewed</Label><Input type="date" value={sourceFormData.last_reviewed_at || ''} onChange={(event) => setSourceFormData(previous => ({ ...previous, last_reviewed_at: event.target.value }))} className="mt-1 bg-white" /></div>
              <div className="sm:col-span-2"><Label className="text-xs">Version label</Label><Input value={sourceFormData.version_label || ''} onChange={(event) => setSourceFormData(previous => ({ ...previous, version_label: event.target.value }))} placeholder="e.g. 2026.2" className="mt-1 bg-white" /></div>
              <ReviewScheduleFields value={sourceFormData.review_schedule || EMPTY_REVIEW_SCHEDULE} onChange={(next) => setSourceFormData((previous) => ({ ...previous, review_schedule: next }))} anchorDate={sourceFormData.last_reviewed_at || sourceFormData.source_published_at || sourceFormData.effective_from} teamName={allTeams.find((team) => team.id === sourceFormData.team_id)?.name} />
            </div>
          )}
        </section>
      </div>
    );
  };

  const getItemTypeMeta = (item) => {
    const sourceType = getSourceTypeDisplay(item.asset_type);
    const customType = customSourceTypes.find(t => t.id === sourceType || `custom_${t.id}` === sourceType);
    const typeConfig = customType || SOURCE_TYPES[sourceType] || SOURCE_TYPES.note;
    const colorClasses = SOURCE_COLORS[sourceType] || SOURCE_COLORS.note;

    return {
      id: sourceType,
      name: item.source_type_name || typeConfig.name || 'Note',
      icon: typeConfig.icon || SOURCE_TYPES.note.icon,
      custom: !!customType,
      colorClasses,
    };
  };

  const getFreshnessMeta = (item) => {
    const today = new Date();
    const starts = item.effective_from ? new Date(`${item.effective_from}T00:00:00`) : null;
    const ends = item.effective_until ? new Date(`${item.effective_until}T23:59:59`) : null;
    if (starts && starts > today) return { label: 'Upcoming', className: 'bg-blue-100 text-blue-700' };
    if (ends && ends < today) return { label: 'Expired', className: 'bg-rose-100 text-rose-700' };
    if (starts || ends) return { label: 'Current', className: 'bg-emerald-100 text-emerald-700' };
    return null;
  };

  const renderBadges = (item, limit) => {
    const badges = limit ? (item.badges || []).slice(0, limit) : (item.badges || []);
    if (badges.length === 0) return null;
    return (
      <div className="flex flex-wrap gap-1">
        {badges.map(badge => (
          <span
            key={badge.id}
            className="rounded-full border px-2 py-0.5 text-[10px] font-medium"
            style={{ borderColor: badge.color, color: badge.color, backgroundColor: `${badge.color}12` }}
          >
            {badge.name}
          </span>
        ))}
        {limit && (item.badges || []).length > limit && (
          <span className="text-[10px] text-gray-400">+{item.badges.length - limit}</span>
        )}
      </div>
    );
  };

  const renderItemTypeIcon = (meta, size = 16) => (
    meta.custom
      ? <IconFromString name={meta.icon} size={size} className="inline-block" />
      : <span className="leading-none">{meta.icon}</span>
  );

  const renderItemDetails = (item, teamInfo) => (
    <div className="mt-3 space-y-3 border-t pt-3">
      <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
        <span className="font-semibold">ID:</span>
        <code className="bg-gray-100 px-2 py-0.5 rounded font-mono text-xs select-all">{item.id}</code>
        <button
          className="text-blue-600 hover:text-blue-800"
          onClick={(e) => {
            e.stopPropagation();
            navigator.clipboard.writeText(item.id);
            toast.success('ID copied to clipboard');
          }}
        >
          Copy
        </button>
      </div>
      {teamInfo && (
        <div className="text-xs text-gray-500">
          <span className="font-semibold">Team Path:</span> {teamInfo.fullPath}
        </div>
      )}
      {item.description && (
        <p className="whitespace-pre-wrap text-sm leading-relaxed text-gray-600">{item.description}</p>
      )}
      {item.keywords?.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {item.keywords.map((k, i) => (
            <span key={i} className="text-xs px-2 py-0.5 bg-gray-100 rounded">{k}</span>
          ))}
        </div>
      )}
      {renderBadges(item)}
      <div className="flex flex-wrap gap-2 text-xs text-gray-500">
        {getFreshnessMeta(item) && (
          <span className={`rounded-full px-2 py-0.5 font-medium ${getFreshnessMeta(item).className}`}>
            {getFreshnessMeta(item).label}
          </span>
        )}
        {item.version_label && <span><strong>Version:</strong> {item.version_label}</span>}
        {item.source_published_at && <span><strong>Published:</strong> {item.source_published_at}</span>}
        {item.last_reviewed_at && <span><strong>Reviewed:</strong> {item.last_reviewed_at}</span>}
        {item.effective_from && <span><strong>From:</strong> {item.effective_from}</span>}
        {item.effective_until && <span><strong>Until:</strong> {item.effective_until}</span>}
      </div>
      {item.pending_change_status === 'pending_review' && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          A newer linked-source version is waiting for review. The approved version remains active.
        </div>
      )}
      <div className="flex flex-wrap gap-2 pt-1 items-center">
        {canEditFile(item.id, item.team_id) ? (
          <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); handleEdit(item); }}>
            <Edit size={14} className="mr-1" /> Edit
          </Button>
        ) : (
          <Button size="sm" variant="outline" disabled title="You don't have edit permission for this item" data-testid={`no-edit-${item.id}`}>
            <Edit size={14} className="mr-1 opacity-50" /> Edit
          </Button>
        )}
        {canDeleteFile(item.id, item.team_id) ? (
          <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); handleDelete(item.id); }} disabled={item.is_protected}>
            <Trash2 size={14} className="mr-1 text-red-500" /> Delete
          </Button>
        ) : (
          <Button size="sm" variant="outline" disabled title="You don't have delete permission for this item" data-testid={`no-delete-${item.id}`}>
            <Trash2 size={14} className="mr-1 opacity-50" /> Delete
          </Button>
        )}
        {user?.role === 'admin' && (
          <Button
            size="sm"
            variant={item.is_protected ? "default" : "outline"}
            className={item.is_protected ? "bg-amber-500 hover:bg-amber-600 text-white" : "text-gray-600"}
            onClick={(e) => { e.stopPropagation(); handleToggleProtection(item.id, item.is_protected); }}
            data-testid={`protect-btn-${item.id}`}
          >
            <Shield size={14} className="mr-1" /> {item.is_protected ? 'Protected' : 'Protect'}
          </Button>
        )}
        {canEditFile(item.id, item.team_id) && (
          <Button
            size="sm"
            variant="outline"
            className="text-blue-600 border-blue-200 hover:bg-blue-50"
            onClick={(e) => { e.stopPropagation(); handleReindex(item); }}
            disabled={item.indexing_status === 'indexing'}
            data-testid={`reindex-btn-${item.id}`}
            title={item.indexing_status === 'failed' ? (item.indexing_error || 'Previous indexing failed - click to retry') : 'Re-generate embeddings so the AI can retrieve from this item'}
          >
            <RefreshCw size={14} className={`mr-1 ${item.indexing_status === 'indexing' ? 'animate-spin' : ''}`} />
            Re-index
          </Button>
        )}
        {item.is_protected && (
          <span className="text-xs text-amber-600 flex items-center gap-1"><Shield size={12} /> Protected</span>
        )}
      </div>
    </div>
  );

  const renderKnowledgeGridCard = (item) => {
    const meta = getItemTypeMeta(item);
    return (
      <button key={item.id} type="button" onClick={() => setSelectedKnowledgeItemId(item.id)} className={`rounded-lg border bg-white p-3 text-left hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 transition-shadow ${meta.colorClasses.border}`} data-testid={`knowledge-card-${item.id}`} aria-label={`View ${item.title}`}>
        <div className="flex items-start justify-between gap-2">
          <span className={`inline-flex h-8 w-8 items-center justify-center rounded-md ${meta.colorClasses.bg} ${meta.colorClasses.text}`}>{renderItemTypeIcon(meta, 16)}</span>
          <StatusDot item={item} />
        </div>
        <p className="mt-2 min-h-[2.5rem] text-sm font-semibold leading-snug text-gray-900" style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{item.title}</p>
        <p className="mt-1 truncate text-[11px] font-medium text-gray-500">{meta.name}</p>
        <div className="mt-1">{renderBadges(item, 2)}</div>
        <div className="mt-3 flex items-center justify-between gap-2 text-[11px] text-gray-500">
          <span className="truncate">
            {item.pending_change_status === 'pending_review'
              ? 'Change awaiting review'
              : getFreshnessMeta(item)?.label || getIndexStatusMeta(item).indexLabel}
          </span>
          <span className="flex-shrink-0">{formatCompactTimeAgo(item.updated_at)}</span>
        </div>
      </button>
    );
  };

  const renderKnowledgeListRow = (item) => {
    const meta = getItemTypeMeta(item);
    const status = getIndexStatusMeta(item);
    return (
      <button key={item.id} type="button" onClick={() => setSelectedKnowledgeItemId(item.id)} className="grid w-full grid-cols-[minmax(0,1fr)_110px_110px_80px_24px] items-center gap-3 border-b border-gray-100 px-3 py-2 text-left hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-indigo-500 last:border-b-0" data-testid={`knowledge-list-item-${item.id}`} aria-label={`View ${item.title}`}>
        <div className="flex min-w-0 items-center gap-2">
          <span className={`inline-flex h-7 w-7 flex-shrink-0 items-center justify-center rounded ${meta.colorClasses.bg} ${meta.colorClasses.text}`}>{renderItemTypeIcon(meta, 14)}</span>
          <div className="min-w-0"><p className="truncate text-sm font-medium text-gray-900">{item.title}</p><p className="truncate text-xs text-gray-500">{meta.name}</p></div>
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-600"><StatusDot item={item} /><span className="truncate">{status.label}</span></div>
        <span className="truncate text-xs text-gray-500">{status.indexLabel}</span>
        <span className="text-xs text-gray-500">{formatCompactTimeAgo(item.updated_at)}</span>
        <Eye size={15} className="text-gray-400" aria-hidden="true" />
      </button>
    );
  };

  const renderKnowledgeItemModal = () => {
    const item = resolutions.find(candidate => candidate.id === selectedKnowledgeItemId);
    if (!item) return null;
    const meta = getItemTypeMeta(item);
    const status = getIndexStatusMeta(item);
    const teamInfo = getTeamInfo(item.team_id);
    return (
      <Dialog open={Boolean(selectedKnowledgeItemId)} onOpenChange={(open) => { if (!open) setSelectedKnowledgeItemId(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="knowledge-item-detail-modal">
          <DialogHeader>
            <div className="flex items-start gap-3 pr-8">
              <span className={`inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${meta.colorClasses.bg} ${meta.colorClasses.text}`}>{renderItemTypeIcon(meta, 18)}</span>
              <div className="min-w-0">
                <DialogTitle className="break-words text-xl text-gray-900">{item.title}</DialogTitle>
                <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-500">
                  <span className={`rounded-full px-2 py-0.5 ${meta.colorClasses.bg} ${meta.colorClasses.text}`}>{meta.name}</span>
                  <span>{status.indexLabel}</span><span aria-hidden="true">·</span>
                  <span>Updated {item.updated_at ? new Date(item.updated_at).toLocaleString() : 'date unavailable'}</span>
                </div>
              </div>
            </div>
          </DialogHeader>
          {renderItemDetails(item, teamInfo)}
        </DialogContent>
      </Dialog>
    );
  };
  const renderKnowledgeListView = () => (
    <div className="max-h-[620px] overflow-auto rounded-lg border bg-white" data-testid="knowledge-items-list">
      <div className="sticky top-0 z-10 grid grid-cols-[minmax(0,1fr)_110px_110px_80px_24px] gap-3 border-b bg-white px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
        <span>Name</span>
        <span>Status</span>
        <span>Index</span>
        <span>Updated</span>
        <span />
      </div>
      {pagedResolutions.map(item => renderKnowledgeListRow(item))}
    </div>
  );

  const renderGroupedKnowledgeItems = () => {
    const sections = activeSourceTypeOptions.map(type => ({
      ...type,
      items: pagedResolutions.filter(item => matchesSourceType(item, type.id)),
    })).filter(section => section.items.length > 0);

    return (
      <div className="space-y-3" data-testid="knowledge-items-grouped">
        {sections.map(section => {
          const isOpen = expandedGroups[section.id] === true;
          const meta = {
            ...section,
            colorClasses: SOURCE_COLORS[section.id] || SOURCE_COLORS.note,
          };

          return (
            <section key={section.id} className="rounded-lg border bg-white">
              <button
                onClick={() => setExpandedGroups(prev => ({ ...prev, [section.id]: !isOpen }))}
                className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-gray-50"
              >
                {isOpen ? <ChevronDown size={16} className="text-gray-400" /> : <ChevronRight size={16} className="text-gray-400" />}
                <span className={`inline-flex h-7 w-7 items-center justify-center rounded ${meta.colorClasses.bg} ${meta.colorClasses.text}`}>
                  {meta.custom ? <IconFromString name={meta.icon} size={14} /> : <span>{meta.icon}</span>}
                </span>
                <span className="font-medium text-gray-900">{section.name}</span>
                <span className="ml-auto rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">{section.items.length}</span>
              </button>
              {isOpen && (
                <div className="border-t p-3">
                  {section.items.length > 0 ? (
                    <div className="grid gap-[10px]" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))' }}>
                      {section.items.map(item => renderKnowledgeGridCard(item))}
                    </div>
                  ) : (
                    <p className="py-8 text-center text-sm text-gray-400">No items in this category yet.</p>
                  )}
                </div>
              )}
            </section>
          );
        })}
      </div>
    );
  };

  const renderGeneralCategoryNode = (category, depth = 0) => {
    const children = browsableGeneralCategories
      .filter(item => (item.parent_id || '') === category.id)
      .sort((a, b) => a.name.localeCompare(b.name));
    const isExpanded = expandedKnowledgeCategories[category.id] === true;
    const categoryAssetCount = categoryDocCounts[category.id] || 0;

    return (
      <div key={category.id}>
        <div className="flex items-center" style={{ paddingLeft: `${Math.min(depth, 5) * 12}px` }}>
          {children.length > 0 ? (
            <button
              type="button"
              onClick={() => setExpandedKnowledgeCategories(previous => ({ ...previous, [category.id]: !isExpanded }))}
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded text-slate-400 hover:bg-slate-100"
              aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${category.name}`}
            >
              {isExpanded ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
            </button>
          ) : <span className="h-7 w-7 shrink-0" />}
          {canManageSharedCategories && (
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                handleDeleteCategory('general', category.id);
              }}
              className="mr-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded text-slate-300 hover:bg-red-50 hover:text-red-600"
              aria-label={`Delete ${category.name}`}
              title={`Delete ${category.name}`}
              data-testid={`delete-general-category-${category.id}`}
            >
              <Trash2 size={12} />
            </button>
          )}
          <button
            type="button"
            onClick={() => { setSelectedGeneralCategory(category); setSelectedTeam(null); setFilterBySourceType(null); }}
            className={`flex min-w-0 flex-1 items-center gap-2 rounded p-2 ${selectedGeneralCategory?.id === category.id ? 'border border-blue-200 bg-blue-50' : 'hover:bg-gray-50'}`}
          >
            <FolderOpen size={13} className="shrink-0 text-blue-500" />
            <span className="flex-1 truncate text-left text-sm">{category.name}</span>
            {categoryAssetCount > 0 && <span className="rounded-full bg-blue-100 px-1.5 py-0.5 text-xs text-blue-700">{categoryAssetCount}</span>}
          </button>
        </div>
        {isExpanded && children.map(child => renderGeneralCategoryNode(child, depth + 1))}
      </div>
    );
  };

  const renderCategoryTree = () => (
    <div className="space-y-1" data-testid="category-tree-view">
      <button
        onClick={() => { setSelectedTeam(null); setSelectedGeneralCategory(null); setFilterBySourceType(null); }}
        className={`w-full flex items-center gap-2 p-2.5 rounded-lg border ${!selectedTeam && !selectedGeneralCategory ? 'bg-gray-900 border-gray-900 text-white' : 'bg-white hover:bg-gray-50'}`}
        data-testid="knowledge-all-items-filter"
      >
        <FolderOpen size={15} />
        <span className="text-sm flex-1 text-left font-medium">All knowledge</span>
        <span className={`text-xs px-1.5 py-0.5 rounded-full ${!selectedTeam && !selectedGeneralCategory ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-600'}`}>{resolutions.length}</span>
      </button>
      {browseCategoryTree.map(sector => (
        <div key={sector.id} className="rounded-lg bg-white border">
          <button
            onClick={() => setExpandedSectors(prev => ({ ...prev, [sector.id]: !prev[sector.id] }))}
            className="w-full flex items-center gap-2 p-3 hover:bg-gray-50"
          >
            {expandedSectors[sector.id] ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            <IconFromString name={sector.icon || 'building-2'} size={16} className="text-orange-500" />
            <span className="font-medium">{sector.name}</span>
            <span className="ml-auto text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded">Sector</span>
          </button>
          
          {expandedSectors[sector.id] && (
            <div className="ml-4 border-l pl-2 pb-2">
              {sector.departments?.map(dept => {
                const deptDocCount = dept.teams?.reduce((sum, t) => sum + (docCountByTeam[t.id] || 0), 0) || 0;
                return (
                <div key={dept.id}>
                  <button
                    onClick={() => setExpandedDepts(prev => ({ ...prev, [dept.id]: !prev[dept.id] }))}
                    className="w-full flex items-center gap-2 p-2 hover:bg-gray-50 rounded"
                  >
                    {expandedDepts[dept.id] ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    <IconFromString name={dept.icon || 'briefcase'} size={14} className="text-purple-500" />
                    <span className="text-sm">{dept.name}</span>
                    <span className="ml-auto flex items-center gap-1.5">
                      {deptDocCount > 0 && <span className="text-[10px] bg-emerald-50 text-emerald-600 px-1.5 py-0.5 rounded border border-emerald-200">{deptDocCount}</span>}
                      <span className="text-xs text-gray-400">{dept.teams?.length || 0} teams</span>
                    </span>
                  </button>
                  
                  {expandedDepts[dept.id] && (
                    <div className="ml-4 border-l pl-2 space-y-1">
                      {dept.teams?.map(team => {
                        const teamDocCount = docCountByTeam[team.id] || 0;
                        return (
                          <button
                            key={team.id}
                            onClick={() => { setSelectedTeam(team); setSelectedGeneralCategory(null); setFilterBySourceType(null); }}
                            className={`w-full flex items-center gap-2 p-2 rounded ${selectedTeam?.id === team.id ? 'bg-blue-50 border border-blue-200' : 'hover:bg-gray-50'}`}
                          >
                            <IconFromString name={team.icon || 'users'} size={12} className="text-green-500" />
                            <span className="text-sm flex-1 text-left">{team.name}</span>
                            {teamDocCount > 0 && (
                              <span className="text-[10px] bg-emerald-50 text-emerald-600 px-1.5 py-0.5 rounded border border-emerald-200">{teamDocCount} item{teamDocCount !== 1 ? 's' : ''}</span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
                );
              })}
            </div>
          )}
        </div>
      ))}
      
      {/* General and unassigned knowledge */}
      <div className="rounded-lg bg-white border mt-3">
        <button
          onClick={() => setExpandedGeneral(!expandedGeneral)}
          className="w-full flex items-center gap-2 p-3 hover:bg-gray-50"
        >
          {expandedGeneral ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          <FolderOpen size={16} className="text-blue-500" />
          <span className="font-medium">Categories &amp; unassigned</span>
          <span className="ml-auto text-[10px] bg-blue-50 text-blue-700 px-2 py-0.5 rounded border border-blue-200">{categorizedDocCount + unassignedDocCount} items</span>
        </button>
        {expandedGeneral && (
          <div className="ml-4 border-l pl-2 pb-2 space-y-1">
            {unassignedDocCount > 0 && <button
              onClick={() => {
                setSelectedGeneralCategory({
                  id: '__unassigned__',
                  name: 'Unassigned',
                  icon: 'folder-open',
                  description: 'Knowledge not assigned to a team',
                });
                setSelectedTeam(null);
                setFilterBySourceType(null);
              }}
              className={`w-full flex items-center gap-2 p-2 rounded ${selectedGeneralCategory?.id === '__unassigned__' ? 'bg-blue-50 border border-blue-200' : 'hover:bg-gray-50'}`}
              data-testid="knowledge-unassigned-filter"
            >
              <FolderOpen size={14} className="text-blue-500" />
              <span className="text-sm flex-1 text-left">Unassigned</span>
              <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full">{unassignedDocCount}</span>
            </button>}
            {browsableGeneralCategories
              .filter(category => !category.parent_id || !browsableGeneralCategories.some(parent => parent.id === category.parent_id))
              .sort((a, b) => a.name.localeCompare(b.name))
              .map(category => renderGeneralCategoryNode(category))}
          </div>
        )}
      </div>

      <p className="text-xs text-gray-400 text-center mt-3 pt-2 border-t border-dashed">
        Teams come from Team &amp; Users. Categories remain document classification.
      </p>
    </div>
  );

  // ============= MAIN RENDER =============
  const supportedFormatLabel = dataSourceInfo?.supported_extensions
    ? Object.keys(dataSourceInfo.supported_extensions).map(ext => ext.replace('.', '').toUpperCase()).join(', ')
    : 'PDF, DOCX, TXT, MD, XLSX, CSV, PNG, JPG, JPEG, TIFF, BMP, WEBP';
  const maxUploadSizeLabel = dataSourceInfo?.max_file_size_mb
    ? `${dataSourceInfo.max_file_size_mb}MB per file`
    : '20MB per file';
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="flex justify-center items-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900" data-testid="knowledge-hub-title">Knowledge Hub</h1>
            <p className="text-sm text-gray-500 mt-0.5">
              <span className="text-gray-700 font-medium">{categoryStats.sectors}</span> Sectors
              <span className="mx-1.5 text-gray-300">|</span>
              <span className="text-gray-700 font-medium">{categoryStats.departments}</span> Departments
              <span className="mx-1.5 text-gray-300">|</span>
              <span className="text-gray-700 font-medium">{categoryStats.teams}</span> Teams
              <span className="mx-1.5 text-gray-300">|</span>
              <span className="text-gray-700 font-medium">{resolutions.length}</span> Knowledge Items
            </p>
          </div>
          <KnowledgeAttentionPanel resolutions={resolutions} onEdit={handleEdit} onChanged={fetchResolutions} openInitially={searchParams.get('attention') === '1'} />
        </div>

        {/* Your sources (connected in Plugins) and the everyday ways to add knowledge */}
        {canManageLinkedSources && <HubSourceShortcuts teams={allTeams} canManage={canManageLinkedSources} canConnect={isAdminOrPlatform(user)} onImported={fetchResolutions} />}
        <section className="mb-5" data-testid="add-knowledge">
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Add knowledge</h3>
          <div className="grid gap-3 md:grid-cols-3">
            <div className="qflow-home-card flex flex-col gap-2 p-3" data-testid="add-knowledge-upload">
              <div className="flex items-center gap-3">
                <span className="qflow-home-icon grid h-9 w-9 shrink-0 place-items-center"><Upload size={17} aria-hidden="true" /></span>
                <div className="min-w-0 flex-1">
                  <p className="flex items-center gap-1 text-sm font-semibold">Upload files<InfoHint label="Upload details">Upload single files or whole folders. Qflow shows upload progress, then indexing, until the knowledge is searchable. Supported: {supportedFormatLabel}. Limit: {maxUploadSizeLabel}.</InfoHint></p>
                  <p className="truncate text-xs text-gray-500">One file or a whole folder</p>
                </div>
              </div>
              {(myPermissions.unrestricted || canAddToCurrentFolder) ? (
                <KnowledgeUploadLauncher
                  user={user}
                  teams={myPermissions.unrestricted ? allTeams : allTeams.filter(team => canEditFolder(team.id))}
                  categories={generalCategories}
                  onCategoriesChange={fetchCategoryTree}
                  initialTeamId={myPermissions.unrestricted ? '' : currentFolderId}
                  requireTeam={!myPermissions.unrestricted}
                  onIngestionComplete={fetchResolutions}
                  triggerClassName="qflow-accent-bg"
                  triggerTestId="data-source-upload-btn"
                />
              ) : (
                <Button variant="outline" size="sm" className="text-amber-700 border-amber-200" onClick={() => toast.info('Select a folder where you have edit access, or ask your Tenant Admin for upload permission.', { duration: 4500 })} data-testid="data-source-upload-locked-btn">
                  <AlertTriangle size={14} className="mr-1.5" /> Access needed
                </Button>
              )}
            </div>
            <button type="button" className="qflow-home-card qflow-home-tile flex items-center gap-3 p-3 text-left" onClick={() => {
              if (myPermissions.unrestricted || canAddToCurrentFolder) openSourceDialog('static');
              else toast.info('Select a folder where you have edit access, or ask your Tenant Admin for permission.', { duration: 4500 });
            }} data-testid="data-source-manual-entry-btn">
              <span className="qflow-home-icon grid h-9 w-9 shrink-0 place-items-center"><FileText size={17} aria-hidden="true" /></span>
              <span className="min-w-0"><span className="block text-sm font-semibold">Write an entry</span><span className="block truncate text-xs text-gray-500">A policy, process or FAQ that isn’t in a file</span></span>
            </button>
            <button type="button" className="qflow-home-card qflow-home-tile flex items-center gap-3 p-3 text-left" onClick={() => {
              if (!(myPermissions.unrestricted || canAddToCurrentFolder)) toast.info('Select a folder where you have edit access, or ask your Tenant Admin for permission.', { duration: 4500 });
              else if (!canManageLinkedSources) toast.info('Ask your Tenant Admin for Manage all knowledge to link pages.', { duration: 4500 });
              else openSourceDialog('linked');
            }} data-testid="data-source-document-url-btn">
              <span className="qflow-home-icon grid h-9 w-9 shrink-0 place-items-center"><Link size={17} aria-hidden="true" /></span>
              <span className="min-w-0"><span className="block text-sm font-semibold">Link a page</span><span className="block truncate text-xs text-gray-500">A web or private document that refreshes</span></span>
            </button>
          </div>
        </section>
        {/* Two Panel Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-[250px_minmax(0,1fr)] gap-5">
          {/* Left Panel - Browse by team */}
          <div>
            <Card className="p-3" data-testid="category-tree-card">
              <h3 className="font-semibold mb-3 text-sm text-gray-700 flex items-center gap-2">
                <FolderOpen size={15} className="text-gray-400" /> Browse by team &amp; category
              </h3>
              {renderCategoryTree()}
            </Card>
          </div>

          {/* Right Panel - Content */}
          <div className="min-w-0 space-y-4">
            {/* Action Bar */}
            <div className="flex items-center justify-between" data-testid="knowledge-action-bar">
              <div>
                {selectedGeneralCategory ? (
                  <>
                    <h3 className="font-semibold text-gray-800"><IconFromString name={selectedGeneralCategory.icon} size={16} className="inline-block mr-1" />{selectedGeneralCategory.name}</h3>
                    <p className="text-xs text-gray-500">{selectedGeneralCategory.description || 'Available to all teams'}</p>
                  </>
                ) : selectedTeam ? (
                  <>
                    <h3 className="font-semibold text-gray-800"><IconFromString name={selectedTeam.icon} size={16} className="inline-block mr-1" />{selectedTeam.name}</h3>
                    <p className="text-xs text-gray-500">{allTeams.find(t => t.id === selectedTeam.id)?.fullPath}</p>
                  </>
                ) : (
                  <>
                    <h3 className="font-semibold text-gray-800">All Items</h3>
                    <p className="text-xs text-gray-500">Select a category or upload files to add knowledge</p>
                  </>
                )}
              </div>

              <div className="flex items-center gap-2">
                {/* Upload Files — unrestricted users always; restricted users (incl. Source Leaders) only in folders they have edit rights on */}
                {/* Permission hint for restricted Source Leaders */}
                {!myPermissions.unrestricted && myPermissions.is_source_lead && !canAddToCurrentFolder && selectedTeam && (
                  <button
                    type="button"
                    onClick={() => toast.info('Contact your Admin to request edit access for this folder.', { duration: 3500 })}
                    className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1 rounded hover:bg-amber-100 hover:border-amber-300 transition-colors cursor-pointer"
                    title="Click to request edit access from Admin"
                    data-testid="rbac-no-edit-hint"
                  >
                    Read-only — request edit access from Admin
                  </button>
                )}

                {/* Add Manual Entry — same gating */}
                {(myPermissions.unrestricted || canAddToCurrentFolder) && (
                <Dialog open={isSourceDialogOpen} onOpenChange={(open) => { setIsSourceDialogOpen(open); if (!open) resetSourceForm(); }}>
                  <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle className="text-xl">{sourceFormData.source_type === 'linked' ? 'Add Public/Private Document URL' : 'Add Manual Source'}</DialogTitle>
                      <p className="text-sm text-gray-500">
                        {sourceFormData.source_type === 'linked'
                          ? 'Retrieve a public or protected URL, preview exactly what Qflow extracted, then save it as searchable knowledge.'
                          : 'Write a knowledge document directly. It will automatically be available for query retrieval.'}
                      </p>
                    </DialogHeader>

                    <form onSubmit={handleSourceSubmit} className="space-y-4">
                      {sourceFormData.source_type === 'linked' && (
                        <div className="rounded-lg border border-indigo-100 bg-indigo-50/30 p-4 space-y-3" data-testid="document-source-type">
                          <div>
                            <Label className="text-sm font-medium">Public/Private document URL</Label>
                            <p className="mt-1 text-xs text-gray-500">Choose public access or provide a protected-site credential. Credentials are encrypted separately from the extracted document.</p>
                          </div>
                          <div className="space-y-3 border-t border-indigo-100 pt-3">
                            <div>
                              <Label className="text-sm">Document URL</Label>
                              <Input value={sourceFormData.source_url} onChange={(e) => { setSourceFormData(prev => ({ ...prev, source_url: e.target.value })); setLinkedFetchState(null); }} placeholder="https://example.com/handbook.pdf" className="mt-1" required data-testid="linked-source-url" />
                            </div>
                            <LinkedSourceAccessFields
                              authMethod={sourceFormData.source_auth_method}
                              auth={sourceFormData.source_auth}
                              onMethodChange={(method) => {
                                setSourceFormData(prev => ({ ...prev, source_auth_method: method, source_auth: {} }));
                                setLinkedFetchState(null);
                              }}
                              onAuthChange={(auth) => {
                                setSourceFormData(prev => ({ ...prev, source_auth: auth }));
                                setLinkedFetchState(null);
                              }}
                            />
                            <div>
                              <Label className="text-sm">Extraction scope</Label>
                              <select
                                value={sourceFormData.extraction_scope === 'custom_selector' ? 'full_page' : (sourceFormData.extraction_scope || 'full_page')}
                                disabled={sourceFormData.extraction_scope === 'custom_selector'}
                                onChange={(e) => { setSourceFormData(prev => ({ ...prev, extraction_scope: e.target.value, custom_selector: '' })); setLinkedFetchState(null); }}
                                className="mt-1 w-full h-9 rounded-md border border-gray-200 bg-white px-3 text-sm disabled:bg-gray-50 disabled:text-gray-400"
                                data-testid="linked-extraction-scope"
                              >
                                <option value="full_page">Whole page</option>
                                <option value="main_content">Main content only</option>
                              </select>
                              <p className="mt-1 text-xs text-gray-500">Main content removes navigation, headers, footers, and other page chrome.</p>
                            </div>
                            <div className="rounded-md border border-gray-100 bg-gray-50/70">
                              <button type="button" className="w-full flex items-center gap-2 px-3 py-2 text-xs text-gray-600" onClick={() => setShowExtractionAdvanced(prev => !prev)} aria-expanded={showExtractionAdvanced}>
                                {showExtractionAdvanced ? <ChevronDown size={14} /> : <ChevronRight size={14} />} Advanced
                              </button>
                              {showExtractionAdvanced && (
                                <div className="border-t border-gray-100 px-3 py-3 space-y-2">
                                  <label className="flex items-center gap-2 text-xs text-gray-700">
                                    <input type="checkbox" checked={sourceFormData.extraction_scope === 'custom_selector'} onChange={(e) => { setSourceFormData(prev => ({ ...prev, extraction_scope: e.target.checked ? 'custom_selector' : 'full_page' })); setLinkedFetchState(null); }} />
                                    Use a custom CSS selector
                                  </label>
                                  <Input value={sourceFormData.custom_selector} disabled={sourceFormData.extraction_scope !== 'custom_selector'} onChange={(e) => { setSourceFormData(prev => ({ ...prev, custom_selector: e.target.value })); setLinkedFetchState(null); }} placeholder="#main-content or article.policy" data-testid="linked-custom-selector" />
                                  <p className="text-[11px] leading-relaxed text-gray-500">Right-click the section in your browser → Inspect → copy its selector. Test it before saving.</p>
                                </div>
                              )}
                            </div>
                            <div>
                              <Label className="text-sm">Refresh frequency</Label>
                              <select value={sourceFormData.refresh_interval} onChange={(e) => setSourceFormData(prev => ({ ...prev, refresh_interval: e.target.value }))} className="mt-1 w-full h-9 rounded-md border border-gray-200 bg-white px-3 text-sm" data-testid="linked-refresh-interval">
                                <option value="daily">Daily</option>
                                <option value="weekly">Weekly</option>
                                <option value="manual_check_only">Manual check only</option>
                              </select>
                            </div>
                            <div className="flex items-center gap-3">
                              <Button type="button" variant="outline" size="sm" disabled={testingLinkedFetch || !sourceFormData.source_url.trim()} onClick={() => handleTestLinkedFetch()} data-testid="linked-test-fetch">
                                {testingLinkedFetch ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <RefreshCw size={14} className="mr-1.5" />} Test and preview
                              </Button>
                            </div>
                            <LinkedSourcePreview state={linkedFetchState} />
                          </div>
                        </div>
                      )}

                      {renderSourceMetadataFields()}

                      {/* Footer */}
                      <div className="flex items-center justify-between pt-4 border-t">
                        <p className="text-xs text-gray-400">
                          {sourceFormData.source_type === 'linked'
                            ? 'Preview is required. After saving, later URL changes remain pending until a user reviews them.'
                            : 'This source will be available for AI query matching immediately after creation.'}
                        </p>
                        <div className="flex gap-2">
                          <Button type="button" variant="outline" onClick={() => setIsSourceDialogOpen(false)}>Cancel</Button>
                          <Button
                            type="submit"
                            disabled={sourceFormData.source_type === 'linked' && !linkedFetchState?.success}
                            className={`${SOURCE_COLORS[selectedSourceType]?.bg?.replace('bg-', 'bg-').replace('-100', '-600')} hover:opacity-90`}
                          >
                            {sourceFormData.source_type === 'linked' ? 'Save and index' : (editingResolution ? 'Update Source' : 'Create Source')}
                          </Button>
                        </div>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
                )}
              </div>
            </div>

            {/* Filters + View Controls */}
            <Card className="p-4" data-testid="knowledge-content-card">
              <div className="mb-4 space-y-3">
                <div className="flex flex-col gap-2 lg:flex-row lg:items-center">
                  <div className="relative min-w-[220px] flex-1">
                    <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                    <Input
                      placeholder="Search..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-8 h-9 text-sm"
                      data-testid="knowledge-search"
                    />
                  </div>
                  <select
                    value={filterBySourceType || ''}
                    onChange={(event) => setFilterBySourceType(event.target.value || null)}
                    className="h-9 min-w-[170px] rounded-md border border-gray-200 bg-white px-3 text-sm text-gray-700"
                    aria-label="Filter by saved document type"
                    data-testid="knowledge-type-filter"
                  >
                    <option value="">All document types</option>
                    {activeSourceTypeOptions.map(type => (
                      <option key={type.id} value={type.id}>{type.name}</option>
                    ))}
                  </select>
                  <select
                    value={itemSort}
                    onChange={(e) => setItemSort(e.target.value)}
                    className="h-9 rounded-md border border-gray-200 bg-white px-3 text-sm text-gray-700"
                    aria-label="Sort knowledge items"
                    data-testid="knowledge-sort"
                  >
                    <option value="recent">Recently updated</option>
                    <option value="name">Name A-Z</option>
                    <option value="status">Status</option>
                  </select>
                  <div className="inline-flex rounded-lg border border-gray-200 bg-gray-100 p-1" data-testid="knowledge-view-switcher">
                    {[
                      { id: 'grid', label: 'Grid', icon: Grid3X3 },
                      { id: 'list', label: 'List', icon: List },
                      { id: 'grouped', label: 'Grouped', icon: Layers },
                    ].map(view => {
                      const ViewIcon = view.icon;
                      return (
                        <button
                          key={view.id}
                          onClick={() => setItemView(view.id)}
                          className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-all ${
                            itemView === view.id
                              ? 'bg-white text-gray-900 shadow-sm'
                              : 'text-gray-600 hover:text-gray-900'
                          }`}
                          data-testid={`knowledge-view-${view.id}`}
                        >
                          <ViewIcon size={14} />
                          {view.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-3 rounded-lg bg-gray-50 px-3 py-2">
                  <label className="flex items-center gap-2 text-xs text-gray-600">
                    Rows per page
                    <select value={itemPageSize} onChange={(event) => setItemPageSize(Number(event.target.value))} className="h-8 rounded-md border border-gray-200 bg-white px-2" data-testid="knowledge-page-size">
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                  </label>
                  <div className="min-w-[280px] flex-1">
                    {renderPagination()}
                  </div>
                </div>
              </div>

              {visibleResolutions.length > 0 ? (
                itemView === 'list' ? (
                  renderKnowledgeListView()
                ) : itemView === 'grouped' ? (
                  renderGroupedKnowledgeItems()
                ) : (
                  <div
                    className="grid gap-[10px]"
                    style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))' }}
                    data-testid="knowledge-items-grid"
                  >
                    {pagedResolutions.map(item => renderKnowledgeGridCard(item))}
                  </div>
                )
              ) : (
                <div className="text-center py-16 text-gray-500" data-testid="knowledge-empty-state">
                  <FolderOpen size={40} className="mx-auto mb-3 text-gray-300" />
                  <p className="font-medium">No items found</p>
                  <p className="text-sm mt-1">Upload files or add a manual entry to build your knowledge base</p>
                </div>
              )}
              {visibleResolutions.length > 0 && <div className="mt-4 border-t border-gray-100 pt-3">{renderPagination()}</div>}
            </Card>
          </div>
        </div>
      </div>

      {renderKnowledgeItemModal()}

      {/* Category Dialogs */}
      <Dialog open={isSectorDialogOpen} onOpenChange={(open) => { setIsSectorDialogOpen(open); if (!open) { setSectorData({ name: '', icon: '🏢', description: '' }); setEditingCategory(null); setEditingCategoryType(null); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle><Building2 className="inline mr-2 text-orange-500" size={20} />{editingCategory ? 'Edit Sector' : 'Add Sector'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveSector} className="space-y-4">
            <div>
              <Label>Name *</Label>
              <Input value={sectorData.name} onChange={(e) => setSectorData({ ...sectorData, name: e.target.value })} required className="mt-1" />
            </div>
            <div>
              <Label>Icon</Label>
              <Input value={sectorData.icon} onChange={(e) => setSectorData({ ...sectorData, icon: e.target.value })} className="mt-1" />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea value={sectorData.description} onChange={(e) => setSectorData({ ...sectorData, description: e.target.value })} className="mt-1" />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setIsSectorDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-orange-600 hover:bg-orange-700">Save</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={isDeptDialogOpen} onOpenChange={(open) => { setIsDeptDialogOpen(open); if (!open) { setDeptData({ name: '', sector_id: '', icon: '💼', description: '' }); setEditingCategory(null); setEditingCategoryType(null); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle><Briefcase className="inline mr-2 text-purple-500" size={20} />{editingCategory ? 'Edit Department' : 'Add Department'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveDept} className="space-y-4">
            <div>
              <Label>Sector *</Label>
              <select value={deptData.sector_id} onChange={(e) => setDeptData({ ...deptData, sector_id: e.target.value })} required className="w-full px-3 py-2 border rounded-lg mt-1">
                <option value="">Select Sector</option>
                {categoryTree.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div>
              <Label>Name *</Label>
              <Input value={deptData.name} onChange={(e) => setDeptData({ ...deptData, name: e.target.value })} required className="mt-1" />
            </div>
            <div>
              <Label>Icon</Label>
              <Input value={deptData.icon} onChange={(e) => setDeptData({ ...deptData, icon: e.target.value })} className="mt-1" />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setIsDeptDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-purple-600 hover:bg-purple-700">Save</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={isTeamDialogOpen} onOpenChange={(open) => { setIsTeamDialogOpen(open); if (!open) { setTeamData({ name: '', department_id: '', icon: '👥', description: '' }); setEditingCategory(null); setEditingCategoryType(null); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle><Users className="inline mr-2 text-green-500" size={20} />{editingCategory ? 'Edit Team' : 'Add Team'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveTeam} className="space-y-4">
            <div>
              <Label>Department *</Label>
              <select value={teamData.department_id} onChange={(e) => setTeamData({ ...teamData, department_id: e.target.value })} required className="w-full px-3 py-2 border rounded-lg mt-1">
                <option value="">Select Department</option>
                {categoryTree.flatMap(s => s.departments || []).map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
            <div>
              <Label>Name *</Label>
              <Input value={teamData.name} onChange={(e) => setTeamData({ ...teamData, name: e.target.value })} required className="mt-1" />
            </div>
            <div>
              <Label>Icon</Label>
              <Input value={teamData.icon} onChange={(e) => setTeamData({ ...teamData, icon: e.target.value })} className="mt-1" />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setIsTeamDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-green-600 hover:bg-green-700">Save</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={isGeneralCategoryDialogOpen} onOpenChange={(open) => { setIsGeneralCategoryDialogOpen(open); if (!open) { setGeneralCategoryData({ name: '', icon: '🌐', description: '' }); setEditingCategory(null); setEditingCategoryType(null); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle><FolderOpen className="inline mr-2 text-blue-500" size={20} />{editingCategory ? 'Edit General Category' : 'Add General Category'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveGeneralCategory} className="space-y-4">
            <div>
              <Label>Name *</Label>
              <Input value={generalCategoryData.name} onChange={(e) => setGeneralCategoryData({ ...generalCategoryData, name: e.target.value })} required className="mt-1" />
            </div>
            <div>
              <Label>Icon</Label>
              <Input value={generalCategoryData.icon} onChange={(e) => setGeneralCategoryData({ ...generalCategoryData, icon: e.target.value })} className="mt-1" />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea value={generalCategoryData.description} onChange={(e) => setGeneralCategoryData({ ...generalCategoryData, description: e.target.value })} className="mt-1" />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setIsGeneralCategoryDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700">Save</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Source Type Dialog */}
      <Dialog open={isSourceTypeDialogOpen} onOpenChange={(open) => { setIsSourceTypeDialogOpen(open); if (!open) { setEditingSourceType(null); setSourceTypeForm({ name: '', icon: '📄', color: 'gray', description: '', custom_fields: [] }); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingSourceType ? 'Edit Source Type' : 'Add Source Type'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSourceTypeSubmit} className="space-y-4">
            <div className="grid grid-cols-4 gap-3">
              <div className="col-span-1">
                <Label className="text-xs text-gray-500">Icon</Label>
                <Input
                  value={sourceTypeForm.icon}
                  onChange={(e) => setSourceTypeForm(prev => ({ ...prev, icon: e.target.value }))}
                  className="text-center text-xl"
                  maxLength={2}
                />
              </div>
              <div className="col-span-3">
                <Label className="text-xs text-gray-500">Name *</Label>
                <Input
                  value={sourceTypeForm.name}
                  onChange={(e) => setSourceTypeForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., FAQ, SOP, Training"
                  required
                />
              </div>
            </div>
            
            <div>
              <Label className="text-xs text-gray-500">Description</Label>
              <Input
                value={sourceTypeForm.description}
                onChange={(e) => setSourceTypeForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Brief description of this source type"
              />
            </div>

            <div>
              <Label className="text-xs text-gray-500">Color</Label>
              <div className="flex gap-2 mt-1">
                {['gray', 'blue', 'green', 'purple', 'amber', 'red'].map(c => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setSourceTypeForm(prev => ({ ...prev, color: c }))}
                    className={`w-8 h-8 rounded-full border-2 ${sourceTypeForm.color === c ? 'border-gray-900' : 'border-transparent'} bg-${c}-400`}
                    style={{ backgroundColor: { gray: '#9ca3af', blue: '#60a5fa', green: '#4ade80', purple: '#c084fc', amber: '#fbbf24', red: '#f87171' }[c] }}
                  />
                ))}
              </div>
            </div>

            {/* Default Fields Notice */}
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-xs font-medium text-gray-600 mb-2">Default Fields (included automatically)</p>
              <div className="flex flex-wrap gap-2">
                {['Assign to Team', 'Category', 'Title *', 'Description *'].map(f => (
                  <span key={f} className="px-2 py-1 bg-white border rounded text-xs text-gray-600">{f}</span>
                ))}
              </div>
            </div>

            {/* Custom Fields */}
            <div>
              <Label className="text-xs text-gray-500 mb-2 block">Custom Fields (optional)</Label>
              
              {/* Existing Custom Fields */}
              {sourceTypeForm.custom_fields.length > 0 && (
                <div className="space-y-2 mb-3">
                  {sourceTypeForm.custom_fields.map((field, idx) => (
                    <div key={idx} className="flex items-center gap-2 p-2 bg-indigo-50 rounded">
                      <span className="text-sm text-indigo-700 flex-1">{field.label}</span>
                      <span className="text-xs text-indigo-500 bg-indigo-100 px-2 py-0.5 rounded">{field.field_type}</span>
                      {field.required && <span className="text-xs text-red-500">*</span>}
                      <Button type="button" size="sm" variant="ghost" className="h-6 w-6 p-0 text-red-500" onClick={() => handleRemoveCustomField(idx)}>
                        <X size={12} />
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              {/* Add New Field */}
              <div className="grid grid-cols-12 gap-2 p-2 border border-dashed rounded">
                <Input
                  value={newCustomField.label}
                  onChange={(e) => setNewCustomField(prev => ({ ...prev, label: e.target.value, name: e.target.value.toLowerCase().replace(/\s+/g, '_') }))}
                  placeholder="Field label"
                  className="col-span-4 text-xs"
                />
                <select
                  value={newCustomField.field_type}
                  onChange={(e) => setNewCustomField(prev => ({ ...prev, field_type: e.target.value }))}
                  className="col-span-3 text-xs border rounded px-2"
                >
                  <option value="text">Text</option>
                  <option value="textarea">Textarea</option>
                  <option value="number">Number</option>
                  <option value="date">Date</option>
                </select>
                <label className="col-span-3 flex items-center gap-1 text-xs">
                  <input
                    type="checkbox"
                    checked={newCustomField.required}
                    onChange={(e) => setNewCustomField(prev => ({ ...prev, required: e.target.checked }))}
                  />
                  Required
                </label>
                <Button type="button" size="sm" onClick={handleAddCustomField} className="col-span-2 text-xs bg-indigo-600 hover:bg-indigo-700">
                  Add
                </Button>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setIsSourceTypeDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700">
                {editingSourceType ? 'Update' : 'Create'} Source Type
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Knowledge Editor */}
      <KnowledgeEditor
        item={editingResolution}
        open={isEditorOpen}
        onOpenChange={(open) => {
          setIsEditorOpen(open);
          if (!open) {
            setEditingResolution(null);
            setInitialVersionId(null);
          }
        }}
        teams={allTeams}
        categories={generalCategories}
        onSave={() => { fetchResolutions(); fetchCategoryTree(); }}
        user={user}
        initialVersionId={initialVersionId}
      />

      {/* Access Control Dialog (Admin only) */}
      <Dialog open={accessControlOpen} onOpenChange={setAccessControlOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="access-control-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield size={18} className="text-indigo-600" />
              Knowledge Hub Access Control
            </DialogTitle>
            <p className="text-xs text-gray-500">
              Grant Source Leaders granular view/edit/delete permissions per folder or per file. Folder permissions act as default inheritance; file-level permissions override when set. Admins always have full access.
            </p>
          </DialogHeader>

          <div className="space-y-4">
            {/* User selector */}
            <div>
              <Label>Select a user</Label>
              <Input
                placeholder="Search by name or email..."
                value={rbacUserSearch}
                onChange={(e) => setRbacUserSearch(e.target.value)}
                className="mt-1"
                data-testid="rbac-user-search"
              />
              <div className="mt-2 max-h-40 overflow-y-auto border rounded-md">
                {rbacUsers
                  .filter(u => {
                    const q = rbacUserSearch.toLowerCase();
                    return !q || (u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q);
                  })
                  .map(u => (
                    <div
                      key={u.id}
                      onClick={() => { setRbacSelectedUserId(u.id); loadPermissionsForUser(u.id); }}
                      className={`p-2 cursor-pointer flex items-center justify-between hover:bg-gray-50 border-b last:border-b-0 ${rbacSelectedUserId === u.id ? 'bg-indigo-50 border-l-2 border-l-indigo-500' : ''}`}
                      data-testid={`rbac-user-option-${u.id}`}
                    >
                      <div>
                        <p className="text-sm font-medium text-gray-800">{u.name}</p>
                        <p className="text-[11px] text-gray-500">{u.email} · <span className="capitalize">{u.role?.replace('_', ' ')}</span></p>
                      </div>
                      {u.is_source_lead && (
                        <span className="text-[10px] bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full">Source Lead</span>
                      )}
                    </div>
                  ))}
                {rbacUsers.length === 0 && (
                  <p className="p-3 text-xs text-gray-500">Loading users...</p>
                )}
              </div>
            </div>

            {rbacSelectedUserId && (
              <>
                {/* Folder Permissions */}
                <Card className="p-4">
                  <h4 className="font-semibold text-sm mb-2 flex items-center gap-2">
                    <FolderOpen size={14} className="text-indigo-600" /> Folder Permissions (default inheritance)
                  </h4>
                  <p className="text-xs text-gray-500 mb-3">Grant access to a team folder. View is always enabled. Toggle edit/delete as needed.</p>
                  <div className="max-h-64 overflow-y-auto space-y-1">
                    {allTeams.map(t => {
                      const existing = rbacFolderPerms.find(p => p.folder_id === t.id);
                      return (
                        <div key={t.id} className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-800 truncate">{t.name}</p>
                            <p className="text-[11px] text-gray-500 truncate">{t.fullPath}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            {existing ? (
                              <>
                                <label className="text-[11px] flex items-center gap-1">
                                  <input
                                    type="checkbox"
                                    checked={existing.can_edit}
                                    onChange={(e) => grantFolderPermission(t.id, 'team', e.target.checked, existing.can_delete)}
                                  /> Edit
                                </label>
                                <label className="text-[11px] flex items-center gap-1">
                                  <input
                                    type="checkbox"
                                    checked={existing.can_delete}
                                    onChange={(e) => grantFolderPermission(t.id, 'team', existing.can_edit, e.target.checked)}
                                  /> Delete
                                </label>
                                <Button size="sm" variant="outline" className="h-7 text-[11px] text-red-600" onClick={() => revokeFolderPermission(existing.id)}>
                                  Revoke
                                </Button>
                              </>
                            ) : (
                              <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => grantFolderPermission(t.id, 'team', false, false)} data-testid={`grant-folder-${t.id}`}>
                                Grant view
                              </Button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                    {allTeams.length === 0 && <p className="text-xs text-gray-400">No folders available.</p>}
                  </div>
                </Card>

                {/* File Permissions */}
                <Card className="p-4">
                  <h4 className="font-semibold text-sm mb-2 flex items-center gap-2">
                    <FileText size={14} className="text-indigo-600" /> File Permissions (override layer)
                  </h4>
                  <p className="text-xs text-gray-500 mb-3">Explicit file-level access. Overrides folder-level defaults for that file.</p>
                  <div className="max-h-64 overflow-y-auto space-y-1">
                    {resolutions.slice(0, 100).map(r => {
                      const existing = rbacFilePerms.find(p => p.file_id === r.id);
                      return (
                        <div key={r.id} className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-800 truncate">{r.title}</p>
                            <p className="text-[11px] text-gray-500 truncate capitalize">{r.source_type === 'linked' ? 'Linked from URL' : (r.asset_type || 'static')}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            {existing ? (
                              <>
                                <label className="text-[11px] flex items-center gap-1">
                                  <input
                                    type="checkbox"
                                    checked={existing.can_edit}
                                    onChange={(e) => grantFilePermission(r.id, e.target.checked, existing.can_delete)}
                                  /> Edit
                                </label>
                                <label className="text-[11px] flex items-center gap-1">
                                  <input
                                    type="checkbox"
                                    checked={existing.can_delete}
                                    onChange={(e) => grantFilePermission(r.id, existing.can_edit, e.target.checked)}
                                  /> Delete
                                </label>
                                <Button size="sm" variant="outline" className="h-7 text-[11px] text-red-600" onClick={() => revokeFilePermission(existing.id)}>
                                  Revoke
                                </Button>
                              </>
                            ) : (
                              <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => grantFilePermission(r.id, false, false)} data-testid={`grant-file-${r.id}`}>
                                Grant view
                              </Button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                    {resolutions.length === 0 && <p className="text-xs text-gray-400">No knowledge items.</p>}
                    {resolutions.length > 100 && <p className="text-[11px] text-gray-500 italic">Showing first 100 items.</p>}
                  </div>
                </Card>
              </>
            )}

            {rbacLoading && <p className="text-center text-sm text-gray-500">Loading permissions...</p>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ResolutionManagement;
